import { SledzenieBazy } from '@/components/SledzenieBazy';
import { usePotwierdzenie } from '@/components/OknoPotwierdzenia';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, ChevronRight, ChevronsDownUp, ChevronsUpDown, Clock, Cloud, Copy, Database, Download, HelpCircle, KeyRound, Loader2, PackageX, Refrigerator, Rocket, ShieldAlert, ShieldCheck, Trash2, Wrench } from 'lucide-react';
import { Fragment, useState } from 'react';
import { z } from 'zod';
import { TokenChmurowegoMastera } from '@/components/TokenChmurowegoMastera';
import {
  umieProsbeOAktualizacje,
  WERSJA_Z_AKTUALIZACJA_NA_ZADANIE,
} from '@/lib/prosba-o-aktualizacje';
import { Badge } from '@/components/ui/badge';
import { WgrajNaZapas } from '@/components/WgrajNaZapas';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast';
import { Dialog, DialogContent, DialogFooter, FormGrid } from '@/components/ui/dialog';
import {
  fetchPrzegladWysp,
  usunWyspe,
  usunWyspeZDanymi,
  type WyspaDoSprzatania,
} from '@/lib/wyspy-sprzatanie';
import { wskazMasteraWyspy } from '@/lib/wyspa-api';
import {
  acceptAgent,
  fetchOdinstalowania,
  fetchOdwolaneProbujace,
  INSTALLER_DOWNLOAD_EXE_PATH,
  INSTALLER_DOWNLOAD_PATH,
  installerInfo,
  issueFoundingCode,
  poprosOAktualizacje,
  issueJoinCode,
  listAgents,
  listEnrollments,
  odinstalujAgenta,
  odinstalujOdwolany,
  rejectAgent,
  usunAgenta,
  revokeEnrollment,
  type Acceptance,
  type AgentRow,
  type WydanyKod,
} from '@/lib/agents-api';
import { API_BASE, apiGet } from '@/lib/api';
import { cn } from '@/lib/utils';
import { opisBazy } from '@/lib/baza-agenta';
import { trybNaLiscieKodow } from '@/lib/tryb-kodu';
import { opisRozjazdu, opisStrefy, opisSynchronizacji, strefaNietypowa } from '@/lib/zegar-agenta';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { kodZNazwy } from '@/lib/kod-wyspy';
import { nazwaZAdresu } from '@/lib/nazwa-z-adresu';
import { fetchPoczekalnia } from '@/lib/poczekalnia-api';
import { useIslands } from '@/lib/islands';

/**
 * Klienci instancji — potrzebni do kodu ZAŁOŻYCIELSKIEGO, który nie ma jeszcze wyspy.
 *
 * Świeża instancja ma jednego klienta (z provisioningu) i ZERO wysp, więc lista wysp jest wtedy
 * pusta i nie da się z niej odczytać klienta. Bez tego zapytania ekran nie pozwoliłby wydać
 * PIERWSZEGO kodu — czyli byłby bezużyteczny dokładnie wtedy, gdy jest najbardziej potrzebny.
 */
const ClientsSchema = z.object({ clients: z.array(z.object({ id: z.string(), name: z.string() })) });

/**
 * AGENCI — kto się zgłosił z maszyn klienta i czy go wpuszczamy.
 *
 * ⚠️ EKRAN NIE JEST ZAWĘŻONY DO BIEŻĄCEJ WYSPY, inaczej niż moduły domenowe. Węzeł do
 * potwierdzenia trzeba znaleźć NIE WIEDZĄC, gdzie się zgłosił: technik dzwoni „zainstalowałem
 * na automacie 7", a nie „zainstalowałem w wyspie o kodzie X". Selektor wyspy kazałby zgadywać,
 * którą wybrać, żeby zobaczyć zgłoszenie — dlatego wyspa jest KOLUMNĄ, nie filtrem.
 *
 * ⚠️⚠️ OCZEKUJĄCY SĄ WIERSZAMI TEJ SAMEJ TABELI, NA ŻÓŁTO — zmienione 28.08.2026 na wprost
 * zgłoszone życzenie: „«Czekają na potwierdzenie» dodaj do tej samej listy i po prostu niech tam
 * będzie na żółto i z opcją zatwierdzenia", a lista agentów „leci na samą górę strony, bo to
 * ważne, żeby widzieć, co jest podłączone".
 *
 * ⚠️ DO TEGO DNIA STAŁO TU ODWROTNIE i powód był dobry, więc zostaje zapisany: „oczekujący są
 * kartami, nie wierszami tabeli. Potwierdzenie to decyzja podejmowana na podstawie sześciu faktów
 * naraz (host, rola, serwer, baza, VMID, kod wejścia). Tabela zmusiłaby do przewijania w poziomie
 * albo do streszczenia ich do jednej etykiety — a wtedy człowiek klikałby «potwierdź», nie widząc
 * tego, co ma ocenić".
 *
 * ⚠️ TA OBAWA JEST ZAADRESOWANA, NIE PORZUCONA: faktów NIE streściliśmy do etykiety — schowały się
 * pod „Szczegóły" w tym samym wierszu, jedno kliknięcie od przycisku „Potwierdź" (patrz stan
 * `szczegoly`). Karta pokazywała je bez klikania, ale kosztem tego, że lista podłączonych agentów
 * była druga na ekranie — a po nią wraca się codziennie, po kod rejestracyjny raz na maszynę.
 */

const IKONA: Record<Acceptance, typeof ShieldCheck> = {
  POTWIERDZONY: ShieldCheck,
  OCZEKUJE: Clock,
  ODRZUCONY: Ban,
  NIEZNANY: HelpCircle,
};

const WARIANT: Record<Acceptance, 'success' | 'warning' | 'destructive' | 'neutral'> = {
  POTWIERDZONY: 'success',
  OCZEKUJE: 'warning',
  ODRZUCONY: 'destructive',
  NIEZNANY: 'neutral',
};

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' }) : '—';

function StanBadge({ stan }: { stan: Acceptance }) {
  const { t } = useI18n();
  const Ikona = IKONA[stan];
  return (
    <Badge variant={WARIANT[stan]}>
      <Ikona className="size-3.5" />
      {t(`agents.state.${stan}`)}
    </Badge>
  );
}

/** Jeden fakt zgłoszony przez agenta. `null` pokazujemy jako „—”, nie ukrywamy wiersza. */
/**
 * Fakt o zegarze albo strefie — z wagą i z POWODEM.
 *
 * ⚠️ Powód jest wpisany w `title`, a nie ukryty w dokumentacji: „Europe/London" przy maszynie
 * w Pile nie wygląda na usterkę dla nikogo, kto nie wie, że wyspa oczekuje Warszawy. Bez zdania
 * wyjaśniającego ten wskaźnik byłby ozdobą.
 */
/**
 * Fakt Z OCENĄ — wartość plus jej waga. Używany tam, gdzie sama liczba albo nazwa nic nie znaczy
 * bez odniesienia: rozjazd zegara, strefa wobec strefy wyspy, baza wobec mastera wyspy.
 */
function FaktZOcena({
  etykieta,
  opis,
  wyroznij = false,
}: {
  etykieta: string;
  opis: { tekst: string; waga: 'ok' | 'uwaga' | 'nieznane'; powod?: string | null };
  wyroznij?: boolean;
}) {
  const kolor =
    opis.waga === 'uwaga'
      ? 'text-[var(--warning)]'
      : opis.waga === 'nieznane'
        ? 'text-muted-foreground'
        : wyroznij
          ? 'font-medium'
          : '';
  return (
    <div className="min-w-0">
      <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{etykieta}</dt>
      <dd className={`truncate font-mono text-xs ${kolor}`} title={opis.powod ?? opis.tekst}>
        {opis.tekst}
      </dd>
    </div>
  );
}

function Fakt({ etykieta, wartosc }: { etykieta: string; wartosc: string | number | null }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{etykieta}</dt>
      <dd className="truncate font-mono text-xs" title={wartosc === null ? undefined : String(wartosc)}>
        {wartosc === null || wartosc === '' ? '—' : wartosc}
      </dd>
    </div>
  );
}

/**
 * WDROŻENIE — kod rejestracyjny i instalator na JEDNYM ekranie.
 *
 * ⚠️ TO JEST SEDNO TEGO EKRANU, a nie ozdoba. Do tej pory kod wydawało się żądaniem HTTP (nie było
 * dla niego ŻADNEGO ekranu), a plik pobierało się z panelu — czyli technik potrzebował dwóch
 * aplikacji i dwóch logowań, żeby dostać dwie rzeczy, które i tak wkleja obok siebie w jednym
 * okienku RDP.
 *
 * ⚠️ INSTANCJA NIE SKŁADA INSTALATORA. Składa panel (ma `makensis` i paczkę Node), instancja tylko
 * przepisuje strumień. Dlatego stan bywa „nie ma pliku" i wtedy ekran prosi o złożenie, zamiast
 * udawać, że przycisk pobierania zadziała.
 */
function Wdrozenie() {
  const { t } = useI18n();
  const { zapytaj, oknoPytania } = usePotwierdzenie();
  const toast = useToast();
  const qc = useQueryClient();
  const { islands, groupId } = useIslands();

  /** Świeżo wydany kod — TYLKO w pamięci strony, nigdy nieodpytywany z API. */
  const [swiezy, setSwiezy] = useState<WydanyKod | null>(null);
  const [tryb, setTryb] = useState<'ZALOZYCIELSKI' | 'WYSPA'>('ZALOZYCIELSKI');
  const [label, setLabel] = useState('');
  const [islandName, setIslandName] = useState('');
  /**
   * ⚠️ KOD WYSPY TRZYMAMY JAKO „NIETKNIĘTY / TKNIĘTY". Dopóki nikt go nie ruszył, liczy się
   * z nazwy przy każdym jej znaku — a gdy człowiek go poprawi, przestajemy nadpisywać jego pracę.
   * Bez tej pamięci pole albo kłamie (stara wartość przy nowej nazwie), albo wymazuje ręczną zmianę.
   */
  const [islandCode, setIslandCode] = useState('');
  const [kodTkniety, setKodTkniety] = useState(false);
  /**
   * MODEL WDROŻENIA WYSPY, KTÓRA POWSTANIE Z TEGO KODU.
   *
   * ⚠️⚠️ TEN WYBÓR JEST NIEODWRACALNY W PRAKTYCE, choć tryb wyspy da się później przełączyć.
   * Prawo maszyny do synchronizacji rozstrzyga się w chwili JEJ rejestracji i nie jest
   * przeliczane, a przy odmowie portal nie przysyła agentowi nawet dostępu do bazy. Wyspa
   * przełączona na Smart Sync PO instalacji maszyn zostawia je bez jednego i bez drugiego —
   * jedynym wyjściem jest ponowna instalacja na każdej z nich (Lockerbox testówki, 24.08.2026).
   *
   * Domyślka `ZWYKLY` to zachowanie całej dotychczasowej floty; Smart Sync jest wyborem świadomym.
   */
  const [trybWyspy, setTrybWyspy] = useState<'ZWYKLY' | 'SMART_SYNC'>('ZWYKLY');
  const [maxUses, setMaxUses] = useState('5');
  const [godziny, setGodziny] = useState('48');
  const [wiecej, setWiecej] = useState(false);

  /**
   * Klient bierzemy z aktywnej wyspy, a gdy wysp nie ma — z jedynego klienta instancji.
   * Świeża instancja ma dokładnie jednego klienta (z provisioningu) i zero wysp, więc bez tego
   * drugiego wariantu ekran nie dałby wydać PIERWSZEGO kodu, czyli po nic by tu był.
   */
  const klienci = islands.length > 0 ? [...new Set(islands.map((i) => i.clientName))] : [];
  const aktywna = islands.find((i) => i.id === groupId) ?? null;

  /**
   * WYPEŁNIENIE POLA NAZWY — z adresu instancji, a gdy z niego nic nie wynika, z nazwy klienta.
   *
   * ⚠️ TO TYLKO PODPOWIEDŹ W EDYTOWALNYM POLU. Instancje stoją pod `vendi.<klient>.ergoflow.app`,
   * więc nazwa klienta jest w adresie, na który technik właśnie patrzy — a mimo to wpisywał ją
   * z klawiatury. Nigdy nie wysyłamy jej bez potwierdzenia: adres bywa tymczasowy, a nazwa wyspy
   * zostaje potem w portalu klienta i w raportach. Patrz `nazwaZAdresu`.
   */
  const podpowiedzNazwy = nazwaZAdresu(window.location.hostname) ?? klienci[0] ?? '';
  const nazwaWyspy = islandName.trim() === '' ? podpowiedzNazwy : islandName.trim();
  /** Kod liczony tak samo jak na serwerze — ekran pokazuje to, co i tak by powstało. */
  const kodWyliczony = kodZNazwy(nazwaWyspy) ?? '';
  const kodDoWyslania = kodTkniety ? islandCode.trim() : kodWyliczony;

  const { data: klientId } = useQuery({
    queryKey: ['klient-instancji'],
    queryFn: async () => {
      const r = await apiGet('/islands', ClientsSchema);
      return r.clients[0]?.id ?? null;
    },
    staleTime: 300_000,
  });

  const instalator = useQuery({
    queryKey: ['installer'],
    queryFn: installerInfo,
    /** Gdy trwa składanie, pytamy częściej — inaczej „gotowe" pojawia się z minutowym opóźnieniem. */
    /* ⚠️ BEZ ODPYTYWANIA W KÓŁKO. Panel składał paczkę minutami i trzeba było czekać na „gotowe";
       teraz plik leży w obrazie i stan nie ma jak się zmienić między odświeżeniami ekranu. */
    refetchInterval: false,
  });

  /**
   * ILE PACZEK CZEKA JUŻ NA POSZCZEGÓLNE KODY.
   *
   * ⚠️⚠️ BEZ TEJ LICZBY JEDYNĄ ODPOWIEDZIĄ na „czy już wgrałem kopię dla tej wyspy?" jest
   * wgranie jej drugi raz — a to kilkaset megabajtów przez łącze klienta. Pytamy BEZ `groupId`,
   * bo te kody wysp jeszcze nie mają.
   */
  const poczekalnia = useQuery({
    queryKey: ['poczekalnia'],
    queryFn: () => fetchPoczekalnia(),
    staleTime: 30_000,
  });

  const kody = useQuery({
    queryKey: ['enrollments', klientId],
    queryFn: () => listEnrollments(klientId!),
    enabled: typeof klientId === 'string',
  });

  const wydaj = useMutation({
    mutationFn: () => {
      /**
       * ⚠️ PUSTYCH PÓL NIE WYSYŁAMY WCALE. Opis kodu i kod wyspy potrafi wyliczyć instancja
       * (`etykietaKodu`, `kodZNazwy`) i tam też rozstrzyga się kolizja kodu — przesłanie pustego
       * napisu odebrałoby jej tę możliwość i wróciłoby błędem walidacji zamiast działać.
       */
      const opis = label.trim();
      const wspolne = {
        ...(opis === '' ? {} : { label: opis }),
        maxUses: Number(maxUses),
        expiresInHours: Number(godziny),
      };
      return tryb === 'ZALOZYCIELSKI'
        ? issueFoundingCode({
            clientId: klientId!,
            islandName: nazwaWyspy,
            ...(kodTkniety && islandCode.trim() !== '' ? { islandCode: islandCode.trim() } : {}),
            /**
             * ⚠️ WYSYŁAMY TYLKO PRZY KODZIE ZAŁOŻYCIELSKIM. Kod dołączający dostaje na to pole
             * odmowę z instancji — i słusznie: wyspa już istnieje i ma swój tryb.
             */
            tryb: trybWyspy,
            ...wspolne,
          })
        : issueJoinCode({ groupId: groupId!, ...wspolne });
    },
    onSuccess: (r) => {
      setSwiezy(r);
      setLabel('');
      setIslandName('');
      setIslandCode('');
      setKodTkniety(false);
      void qc.invalidateQueries({ queryKey: ['enrollments'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const odwolaj = useMutation({
    mutationFn: (id: string) => revokeEnrollment(id),
    onSuccess: (r) => {
      toast.success(r.alreadyRevoked ? t('agents.code.alreadyRevoked') : t('agents.code.revoked'));
      void qc.invalidateQueries({ queryKey: ['enrollments'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const inst = instalator.data;
  const mb = (b: number | null | undefined) => (typeof b === 'number' ? (b / 1024 / 1024).toFixed(1) : '—');

  /**
   * ⚠️ WARUNEK PILNUJE TEGO, CZEGO NIE DA SIĘ WYLICZYĆ. Wcześniej wymagał opisu kodu (≥3 znaki)
   * i poprawnego kodu wyspy wpisanego ręcznie — czyli dwóch wartości, które instancja policzy sama.
   * Zostaje nazwa wyspy: tej zgadywać nie wolno, bo zobaczy ją klient w portalu.
   */
  const gotowyDoWydania =
    tryb === 'WYSPA'
      ? groupId !== null
      : typeof klientId === 'string' && nazwaWyspy.length >= 2 && /^[a-z0-9][a-z0-9._-]*$/.test(kodDoWyslania);

  return (
    <section className="space-y-4 rounded-lg border border-border bg-card p-4">
      {oknoPytania}
      <header className="space-y-1">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <Rocket className="size-4" />
          {t('agents.deployTitle')}
        </h2>
      </header>

      {/*
        ⚠️ KOD SZEROKI, INSTALATOR WASKI — zgloszone: „ustaw to wszystko w pionie i wezsze, tak zeby
        bylo wiecej miejsca na «Kod rejestracyjny»". Polowa na polowe dawala instalatorowi tyle samo
        miejsca co formularzowi z czterema polami i wydanym kodem, a instalator to dwa przyciski
        i szesc liczb.
      */}
      <div className="grid gap-4 lg:grid-cols-[1fr_19rem]">
        {/* ── 1. KOD ────────────────────────────────────────────────────────── */}
        <div className="space-y-3 rounded-md border border-border p-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('agents.code.title')}
          </h3>

          <div className="flex flex-wrap gap-2 text-xs">
            {(['ZALOZYCIELSKI', 'WYSPA'] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setTryb(k)}
                className={`rounded border px-2 py-1 ${tryb === k ? 'border-primary bg-primary/10 font-medium' : 'border-border text-muted-foreground'}`}
              >
                {t(`agents.code.mode.${k}`)}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground">{t(`agents.code.modeHint.${tryb}`)}</p>

          {/**
            * ⚠️ KAŻDE POLE MA ZDANIE „CO TU WPISUJESZ", nie samą nazwę. Stały tu trzy etykiety —
            * „Opis kodu", „Nazwa wyspy", „Kod wyspy" — z których nie wynikało ani co jest czym,
            * ani gdzie ta wartość potem widnieje. Nazwa pola nie jest instrukcją.
            */}
          <div className="grid gap-3">
            {tryb === 'ZALOZYCIELSKI' ? (
              <>
                <label className="flex flex-col gap-1 text-xs">
                  <span className="font-medium">{t('agents.code.islandName')}</span>
                  <span className="text-[11px] leading-snug text-muted-foreground">{t('agents.code.islandNameWhat')}</span>
                  <Input
                    className="h-8"
                    value={islandName}
                    onChange={(e) => setIslandName(e.target.value)}
                    placeholder={podpowiedzNazwy || t('agents.code.islandNamePlaceholder')}
                  />
                  {islandName.trim() === '' && podpowiedzNazwy !== '' && (
                    <span className="text-[11px] text-muted-foreground">
                      {t('agents.code.zAdresu').replace('{nazwa}', podpowiedzNazwy)}
                    </span>
                  )}
                </label>

                <label className="flex flex-col gap-1 text-xs">
                  <span className="font-medium">{t('agents.code.islandCode')}</span>
                  <span className="text-[11px] leading-snug text-muted-foreground">{t('agents.code.islandCodeWhat')}</span>
                  <Input
                    className="h-8 font-mono"
                    value={kodTkniety ? islandCode : kodWyliczony}
                    onChange={(e) => {
                      setKodTkniety(true);
                      setIslandCode(e.target.value.toLowerCase());
                    }}
                    placeholder="zaklad-pila"
                  />
                  {!kodTkniety && kodWyliczony !== '' && (
                    <span className="text-[11px] text-muted-foreground">{t('agents.code.kodZNazwy')}</span>
                  )}
                </label>

                {/**
                  * ⚠️⚠️ MODEL WDROŻENIA WYBIERA SIĘ TUTAJ, A NIE PO INSTALACJI MASZYN — i to jest
                  * naprawa awarii z Lockerboksa (24.08.2026), nie wygoda ekranu. Prawo maszyny do
                  * synchronizacji rozstrzyga się w chwili JEJ rejestracji i nie jest przeliczane;
                  * przy odmowie agent nie dostaje nawet dostępu do bazy. Wyspa przełączona na Smart
                  * Sync PÓŹNIEJ zostawia maszyny bez jednego i bez drugiego, a jedynym wyjściem jest
                  * ponowna instalacja na każdej z nich.
                  *
                  * ⚠️ POLE STOI NA WIERZCHU, NIE POD „WIĘCEJ OPCJI". Schowanie go znaczyłoby, że
                  * wdrożenie Smart Synca zaczyna się od kliknięcia, o którym trzeba wiedzieć.
                  */}
                <div className="flex flex-col gap-1 text-xs">
                  <span className="font-medium">{t('agents.code.trybWyspy')}</span>
                  <span className="text-[11px] leading-snug text-muted-foreground">{t('agents.code.trybWyspyWhat')}</span>
                  <div className="flex flex-wrap gap-2 pt-0.5">
                    {(['ZWYKLY', 'SMART_SYNC'] as const).map((k) => (
                      <button
                        key={k}
                        type="button"
                        onClick={() => setTrybWyspy(k)}
                        className={`rounded border px-2 py-1 ${trybWyspy === k ? 'border-primary bg-primary/10 font-medium' : 'border-border text-muted-foreground'}`}
                      >
                        {t(`agents.code.trybWyspy.${k}`)}
                      </button>
                    ))}
                  </div>
                  <span className="text-[11px] leading-snug text-muted-foreground">
                    {t(`agents.code.trybWyspyHint.${trybWyspy}`)}
                  </span>
                </div>

                {klienci.length > 1 && <p className="text-[11px] text-[var(--warning)]">{t('agents.code.manyClients')}</p>}
              </>
            ) : (
              <p className="text-[11px] text-muted-foreground">
                {aktywna === null ? t('agents.code.noIsland') : `${t('agents.code.forIsland')}: ${aktywna.name} (${aktywna.code})`}
              </p>
            )}

            {/**
              * OPIS KODU ZOSTAJE, ALE JAKO NIEOBOWIĄZKOWY I NA KOŃCU — nie jest tożsamością niczego,
              * tylko notatką na liście wydanych kodów. Pusty = instancja wpisze nazwę wyspy.
              */}
            <label className="flex flex-col gap-1 text-xs">
              <span className="font-medium">{t('agents.code.label')}</span>
              <span className="text-[11px] leading-snug text-muted-foreground">{t('agents.code.labelWhat')}</span>
              <Input
                className="h-8"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder={tryb === 'ZALOZYCIELSKI' ? `${nazwaWyspy || '—'} — nowa wyspa` : (aktywna?.name ?? '—')}
              />
            </label>

            {/**
              * ⚠️ LIMIT UŻYĆ I TERMIN SCHOWANE, ALE NIE USUNIĘTE. Domyślne 5 maszyn na 48 godzin
              * pokrywa zwykłe wdrożenie na obiekcie, a obie wartości są tu bramką bezpieczeństwa
              * (kod, który wyciekł, ma przestać działać) — więc muszą zostać widoczne dla kogoś,
              * kto ich świadomie potrzebuje, i niewidoczne dla kogoś, kto po prostu wdraża agenta.
              */}
            {wiecej ? (
              <div className="flex gap-2">
                <label className="flex flex-1 flex-col gap-1 text-xs">
                  <span className="font-medium">{t('agents.code.maxUses')}</span>
                  <span className="text-[11px] leading-snug text-muted-foreground">{t('agents.code.maxUsesWhat')}</span>
                  <Input className="h-8" type="number" min={1} max={200} value={maxUses} onChange={(e) => setMaxUses(e.target.value)} />
                </label>
                <label className="flex flex-1 flex-col gap-1 text-xs">
                  <span className="font-medium">{t('agents.code.hours')}</span>
                  <span className="text-[11px] leading-snug text-muted-foreground">{t('agents.code.hoursWhat')}</span>
                  <Input className="h-8" type="number" min={1} max={720} value={godziny} onChange={(e) => setGodziny(e.target.value)} />
                </label>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setWiecej(true)}
                className="self-start text-[11px] text-muted-foreground underline decoration-dotted hover:text-foreground"
              >
                {t('agents.code.more').replace('{maszyn}', maxUses).replace('{godzin}', godziny)}
              </button>
            )}
          </div>

          <Button size="sm" disabled={!gotowyDoWydania || wydaj.isPending} onClick={() => wydaj.mutate()}>
            {wydaj.isPending ? <Loader2 className="size-4 animate-spin" /> : <KeyRound className="size-4" />}
            {t('agents.code.issue')}
          </Button>

          {swiezy !== null && (
            /**
             * ⚠️ TREŚĆ KODU ISTNIEJE TYLKO TERAZ. W bazie leży SHA-256, więc nie ma trasy
             * „pokaż ponownie" — i ekran musi to powiedzieć PRZED zamknięciem, bo inaczej
             * pierwszą reakcją na zgubiony kod będzie szukanie go w portalu.
             */
            <div className="space-y-2 rounded-md border border-[var(--warning)] bg-[color-mix(in_oklab,var(--warning)_8%,transparent)] p-3">
              <p className="flex items-center gap-2 text-xs font-medium">
                <ShieldAlert className="size-4" />
                {t('agents.code.onceWarning')}
              </p>
              <code className="block overflow-x-auto rounded bg-background p-2 font-mono text-xs">{swiezy.code}</code>
              <p className="text-[11px] text-muted-foreground">{swiezy.note}</p>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    void navigator.clipboard.writeText(swiezy.code).then(
                      () => toast.success(t('agents.code.copied')),
                      // schowek bywa zablokowany (brak HTTPS, polityka przeglądarki) — kod
                      // zostaje wtedy na ekranie do przepisania, a nie ginie po cichu
                      () => toast.error(t('agents.code.copyFailed')),
                    );
                  }}
                >
                  <Copy className="size-4" />
                  {t('agents.code.copy')}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSwiezy(null)}>
                  {t('agents.code.hide')}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* ── 2. INSTALATOR ─────────────────────────────────────────────────── */}
        <div className="space-y-3 rounded-md border border-border p-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('agents.inst.title')}
          </h3>

          {instalator.isPending ? (
            <p className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              {t('common.loading')}
            </p>
          ) : inst?.configured === false ? (
            /** Brak konfiguracji NIE jest awarią — ekran mówi, czego brakuje. */
            <p className="text-xs text-muted-foreground">{inst.note}</p>
          ) : (
            <>
              {/* ⚠️ JEDNA KOLUMNA: w waskiej kolumnie dwie lamalyby etykiety w polowie slowa. */}
              <dl className="grid grid-cols-1 gap-y-2">
                <Fakt etykieta={t('agents.inst.version')} wartosc={inst?.packageVersion ?? inst?.targetVersion ?? null} />
                {/**
                  * ⚠️ WERSJA WYDAWANA KANAŁEM — druga liczba, której na tym ekranie brakowało.
                  * Bez niej jedynym numerem widocznym na froncie była wersja z archiwum
                  * instalatora, więc „agent ma 0.6.96" nie miało się do czego odnieść.
                  */}
                <Fakt etykieta={t('agents.inst.channel')} wartosc={inst?.channelVersion ?? null} />
                <Fakt etykieta={t('agents.inst.size')} wartosc={inst?.ready ? `${mb(inst.sizeBytes)} MB` : null} />
                <Fakt etykieta={t('agents.inst.file')} wartosc={inst?.fileName ?? null} />
                <Fakt etykieta={t('agents.inst.node')} wartosc={inst?.wersjaNode ?? null} />
                <Fakt etykieta={t('agents.inst.builtOn')} wartosc={inst?.zbudowano ?? null} />
              </dl>

              {inst?.ready === true ? (
                <div className="flex flex-col gap-2">
                  {/*
                    ⚠️⚠️ `.exe` JEST TERAZ DROGA GLOWNA — zgloszone: „instalator .exe juz jest po
                    testach, wiec wywal z niego (beta), przestaw go na miejsce zipa i daj niebieski
                    kolor. Zip idzie (…) z dopiskiem (legacy)".

                    ⚠️ ODWRACA DECYZJE ZAPISANA TU DO 28.08.2026: „`.zip` zostaje pierwszy
                    i domyslny, bo ta droga jest sprawdzona na wdrozeniach; `.exe` z kreatorem stoi
                    obok, wyraznie oznaczony jako beta (…) Gdy beta sie obroni, zostaje sam `.exe`".
                    Beta sie obronila, wiec zamiana kolejnosci jest wykonaniem tamtego zdania,
                    a nie zmiana zdania. `.zip` zostaje jako `legacy`, bo na maszynach, gdzie juz
                    lezy rozpakowany, technik moze go potrzebowac.

                    ⚠️ JEDEN POD DRUGIM, nie obok siebie: kolumna jest waska (patrz siatka wyzej),
                    a przy dwoch przyciskach w rzedzie ten drugi zawijalby sie i tak.

                    ⚠️ ZWYKLY ODSYLACZ, NIE `fetch`: pobranie 48 MB przez przegladarke ma byc
                    pobraniem, z paskiem postepu i wznawianiem, a nie blobem w pamieci karty.

                    ⚠️ WARUNEK NA `exeFileName`, nie na wersji instancji: obraz z wczesniejszego
                    wydania nie niesie tego pliku, a ekran ma wtedy pokazac jedna droge i milczec
                    o drugiej, zamiast dawac odsylacz konczacy sie bledem.
                  */}
                  {inst?.exeFileName ? (
                    <a href={`${API_BASE}${INSTALLER_DOWNLOAD_EXE_PATH}`}>
                      {/*
                        ⚠️ OSTRZEZENIE SMARTSCREENA ZOSTAJE W PODPOWIEDZI, choc opisy z ekranu
                        wypadly. Pliku nie podpisujemy certyfikatem, wiec technik zobaczy na maszynie
                        klienta czerwone okno „Windows protected your PC" — a najgorszy moment na
                        rozmowe „czy to wirus" to ten, w ktorym stoi przy automacie. Tresc nie zajmuje
                        juz miejsca na ekranie, ale jest jedno najechanie kursorem od przycisku.
                      */}
                      <Button size="sm" className="w-full" title={t('agents.inst.smartscreen')}>
                        <Download className="size-4" />
                        {t('agents.inst.downloadExe')}
                      </Button>
                    </a>
                  ) : null}
                  <a href={`${API_BASE}${INSTALLER_DOWNLOAD_PATH}`}>
                    <Button size="sm" variant="outline" className="w-full">
                      <Download className="size-4" />
                      {t('agents.inst.download')}
                      <Badge variant="neutral">{t('agents.inst.legacy')}</Badge>
                    </Button>
                  </a>
                </div>
              ) : (
                /**
                 * ⚠️ NIE MA TU JUŻ PRZYCISKU „ZŁÓŻ INSTALATOR". Panel, który składał paczkę na
                 * żądanie, odszedł — archiwum jest częścią obrazu, więc jego brak to błąd wydania,
                 * a nie stan, który operator może naprawić kliknięciem.
                 */
                <p className="rounded border border-destructive/40 bg-destructive/10 p-2 text-[11px] text-destructive">
                  {inst?.note ?? t('agents.inst.notReady')}
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── 3. WYDANE KODY ──────────────────────────────────────────────────── */}
      {(kody.data?.enrollments.length ?? 0) > 0 && (
        <div className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('agents.code.listTitle')}
          </h3>
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-left text-[11px]">
              <thead className="bg-muted/40 uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="p-2 font-medium">{t('agents.code.colLabel')}</th>
                  <th className="p-2 font-medium">{t('agents.code.colKind')}</th>
                  {/*
                    ⚠️ KOLUMNA ZE ZGŁOSZENIA (31.08.2026): „nie wiadomo, czy ktoś założył jako
                    zwykły czy smart sync". Wprost obok rodzaju kodu, bo to dwie różne rzeczy:
                    rodzaj mówi, co kod ROBI (zakłada / dołącza), a to — jak wyspa JEST wdrożona.
                  */}
                  <th className="p-2 font-medium">{t('agents.code.colModel')}</th>
                  <th className="p-2 font-medium">{t('agents.code.colUses')}</th>
                  <th className="p-2 font-medium">{t('agents.code.colExpires')}</th>
                  <th className="p-2 font-medium">{t('agents.code.colDenied')}</th>
                  <th className="p-2" />
                </tr>
              </thead>
              <tbody>
                {kody.data!.enrollments.map((k) => {
                  const odmowy = k.uses.filter((u) => !u.accepted).length;
                  const martwy = k.revokedAt !== null || (k.expiresAt !== null && new Date(k.expiresAt) <= new Date());
                  return (
                    <tr key={k.id} className={`border-t border-border ${martwy ? 'text-muted-foreground' : ''}`}>
                      <td className="p-2">
                        {k.label}
                        <br />
                        <span className="font-mono text-muted-foreground">{k.prefix}…</span>
                      </td>
                      <td className="p-2">
                        {k.founding ? t('agents.code.kindFounding') : t('agents.code.kindJoin')}
                        {k.founding && k.groupId === null && (
                          <>
                            <br />
                            <span className="text-[var(--warning)]">{t('agents.code.notFoundedYet')}</span>
                          </>
                        )}
                      </td>
                      {/*
                        ⚠️ TRYB BIERZEMY Z `trybNaLiscieKodow`, a nie z `k.tryb` — przy kodzie
                        DOŁĄCZAJĄCYM pole na kodzie zostaje domyślne („zwykły") i skłamałoby przy
                        każdej wyspie Smart Sync. Uzasadnienie w tamtym module.
                      */}
                      <td className="p-2">
                        {(() => {
                          const m = trybNaLiscieKodow(k);
                          return (
                            <>
                              {t(`agents.code.trybKrotki.${m.tryb}`)}
                              {m.zZalozenia && (
                                <>
                                  <br />
                                  <span className="text-muted-foreground">{t('agents.code.modelZalozy')}</span>
                                </>
                              )}
                            </>
                          );
                        })()}
                      </td>
                      <td className="p-2">
                        {k.useCount} / {k.maxUses}
                      </td>
                      <td className="p-2">{k.revokedAt !== null ? t('agents.code.revokedAt') : when(k.expiresAt)}</td>
                      <td className="p-2">
                        {/** Odmowy pokazujemy WYRÓŻNIONE: znaczą, że ktoś użył pliku poza wdrożeniem. */}
                        {odmowy > 0 ? <span className="font-medium text-destructive">{odmowy}</span> : '—'}
                      </td>
                      <td className="p-2">
                        {/*
                          ══════════════════════════════════════════════════════
                           KOPIA NA ZAPAS — TYLKO PRZY KODZIE, KTÓRY WYSPY JESZCZE NIE ZAŁOŻYŁ.

                           ⚠️⚠️ ZAMÓWIONE 08.09.2026: „żeby był gotówy jak ona powstanie". Wyspy nie da
                           się utworzyć z portalu — rodzi się przy pierwszej rejestracji — więc kod jest
                           jedyną rzeczą, która przed przyjazdem maszyny wie, jak ta wyspa będzie się
                           nazywać.

                           ⚠️ PO ZAŁOŻENIU WYSPY PRZYCISK ZNIKA I TO JEST ŚWIADOME: paczkę wgrywa się
                           wtedy na ekranie kopii tej wyspy, razem z całą resztą obsługi kopii.
                           Dwa miejsca do tego samego znaczyłyby, że trzeba wiedzieć, które jest
                           właściwe teraz.
                          ══════════════════════════════════════════════════════
                        */}
                        {k.founding && k.groupId === null && k.revokedAt === null && (
                          <WgrajNaZapas
                            kodId={k.id}
                            juzCzeka={
                              (poczekalnia.data ?? []).filter(
                                (paczka) =>
                                  paczka.przejeta === null &&
                                  paczka.przypiecie.rodzaj === 'KOD' &&
                                  paczka.przypiecie.id === k.id,
                              ).length
                            }
                          />
                        )}
                        {k.revokedAt === null && (
                          /*
                            ⚠️ PYTANIE PRZED ODWOŁANIEM — dołożone 28.08.2026 razem z tym samym
                            w tokenach panelu. Skutek jest tu WĘŻSZY niż przy tokenie agenta i pytanie
                            mówi to wprost: kod przestaje wpuszczać KOLEJNE maszyny, a te, które już
                            weszły, działają dalej na własnych tokenach. Bez tego zdania człowiek musi
                            zgadywać, czy właśnie odciął działającą flotę — a to jest dokładnie ten
                            rodzaj niepewności, po którym nie klika się niczego.
                          */
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={odwolaj.isPending}
                            onClick={async () => {
                              const pytanie = t('agents.code.revokePytanie')
                                .replace('{label}', k.label)
                                .replace('{prefiks}', k.prefix)
                                .replace('{uzyc}', `${k.useCount} / ${k.maxUses}`);
                              const tytul = t('agents.code.revoke');
                              if (!(await zapytaj({ tytul, tresc: pytanie, etykietaPotwierdz: tytul, ton: 'grozne' }))) return;
                              odwolaj.mutate(k.id);
                            }}
                          >
                            <Ban className="size-4" />
                            {t('agents.code.revoke')}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ODWOŁANE, WCIĄŻ PRÓBUJĄ — osobna sekcja pod listą agentów (Gestamp Września, 08.10.2026).
 *
 *  ⚠️⚠️ ZGŁOSZONE PRZEZ KAMILA: po wymianie komputera przy maszynie 4 stary komputer pukał odwołanym
 *  tokenem, a ostrzeżenie „wciskało się" w wiersz NOWEGO, sprawnego węzła o tej samej nazwie. „Może
 *  pojawić się taka informacja na liście agentów, w jakiejś podzakładce" — to jest ta podzakładka.
 *
 *  ⚠️ JEDNA CZYNNOŚĆ ZDALNA (od 13.10.2026): „Odinstaluj" — zlecenie dla TEGO tokenu, które maszyna
 *  dostanie przy najbliższym pukaniu (`410`, patrz `odinstalowanie.ts` w API). Węzeł o tej samej
 *  nazwie z działającym tokenem (nowy komputer) zostaje nietknięty. Reszta naprawy dzieje się przy
 *  maszynie, a kolumna „Co zrobić" mówi która. Sekcji nie ma, gdy nikt nie próbuje.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
function OdwolaneProbujace() {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['agents', 'odwolane'], queryFn: fetchOdwolaneProbujace, refetchInterval: 60_000 });
  const odinstaluj = useMutation({
    mutationFn: (tokenId: string) => odinstalujOdwolany(tokenId),
    onSuccess: (r) => {
      toast.success(t('agents.odinstaluj.zlecone').replace('{wezel}', r.nodeId));
      void qc.invalidateQueries({ queryKey: ['agents'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const wiersze = data?.rows ?? [];
  if (wiersze.length === 0) return null;
  return (
    <section className="space-y-2">
      <h2 className="flex items-center gap-2 text-sm font-semibold">
        <Ban className="size-4 text-[var(--warning)]" />
        {t('agents.odwolane.tytul')}
      </h2>
      <p className="text-xs text-muted-foreground">{t('agents.odwolane.opis')}</p>
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-xs">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="px-3 py-2 font-medium">{t('agents.odwolane.wezel')}</th>
              <th className="px-3 py-2 font-medium">{t('agents.odwolane.wyspa')}</th>
              <th className="px-3 py-2 font-medium">{t('agents.odwolane.odwolany')}</th>
              <th className="px-3 py-2 font-medium">{t('agents.odwolane.od')}</th>
              <th className="px-3 py-2 font-medium">{t('agents.odwolane.ostatnio')}</th>
              <th className="px-3 py-2 font-medium">{t('agents.odwolane.adres')}</th>
              <th className="px-3 py-2 font-medium">{t('agents.odwolane.coZrobic')}</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {wiersze.map((r) => (
              <tr key={r.id} className="border-t border-border/60">
                <td className="px-3 py-2">
                  <div className="font-mono">{r.nodeId}</div>
                  <code className="font-mono text-[11px] text-muted-foreground">{r.prefix}…</code>
                </td>
                <td className="px-3 py-2">{r.wyspa.name}</td>
                <td className="px-3 py-2 text-muted-foreground">
                  {r.powod === 'WYGASL' ? `${t('agents.odwolane.wygasl')} ${when(r.expiresAt ?? null)}` : when(r.revokedAt ?? null)}
                </td>
                <td className="px-3 py-2 text-muted-foreground">{when(r.firstDeniedAt ?? null)}</td>
                <td className="px-3 py-2">{when(r.lastDeniedAt ?? null)}</td>
                <td className="px-3 py-2 font-mono">{r.lastDeniedIp ?? '—'}</td>
                <td className="px-3 py-2">
                  {r.wezelMaKlucz ? t('agents.odwolane.staraMaszyna') : t('agents.odwolane.bezKlucza')}
                </td>
                <td className="px-3 py-2">
                  <Button size="sm" variant="ghost" disabled={odinstaluj.isPending} onClick={() => odinstaluj.mutate(r.id)}>
                    <PackageX className="size-4" />
                    {t('agents.odinstaluj.przycisk')}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ODINSTALOWANIA — zlecenia z ostatnich 60 dni (13.10.2026).
 *
 *  ⚠️ PO TO, ŻEBY USUNIĘTY WĘZEŁ NIE ZNIKAŁ BEZ ŚLADU: węzeł schodzi z listy agentów od razu, a maszyna
 *  odinstaluje się dopiero, gdy zapuka. Tu widać, czy zapukała i czy przyjęła polecenie — to ostatni
 *  meldunek, jaki wysyła. Sekcji nie ma, gdy nie ma zleceń.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
function Odinstalowania() {
  const { t } = useI18n();
  const { data } = useQuery({ queryKey: ['agents', 'odinstalowania'], queryFn: fetchOdinstalowania, refetchInterval: 60_000 });
  const wiersze = data?.rows ?? [];
  if (wiersze.length === 0) return null;
  return (
    <section className="space-y-2">
      <h2 className="flex items-center gap-2 text-sm font-semibold">
        <PackageX className="size-4 text-muted-foreground" />
        {t('agents.odinstalowania.tytul')}
      </h2>
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-xs">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="px-3 py-2 font-medium">{t('agents.odwolane.wezel')}</th>
              <th className="px-3 py-2 font-medium">{t('agents.odwolane.wyspa')}</th>
              <th className="px-3 py-2 font-medium">{t('agents.odinstalowania.zlecono')}</th>
              <th className="px-3 py-2 font-medium">{t('agents.odinstalowania.stan')}</th>
            </tr>
          </thead>
          <tbody>
            {wiersze.map((r) => (
              <tr key={r.id} className="border-t border-border/60 align-top">
                <td className="px-3 py-2">
                  <div className="font-mono">{r.nodeId}</div>
                  {r.hostname != null && <div className="text-muted-foreground">{r.hostname}</div>}
                </td>
                <td className="px-3 py-2">{r.wyspa}</td>
                <td className="px-3 py-2 text-muted-foreground">
                  {when(r.zlecono)}
                  <br />
                  {r.zlecilEmail}
                </td>
                <td className="px-3 py-2">
                  {r.przyjetoO != null ? (
                    <>
                      <Badge variant="success">{t('agents.odinstalowania.przyjete')}</Badge> {when(r.przyjetoO)}
                      {r.meldunek != null && <p className="mt-1 text-muted-foreground">{r.meldunek}</p>}
                    </>
                  ) : r.ostatniaProbaO != null ? (
                    <span>
                      {t('agents.odinstalowania.pukala')} {when(r.ostatniaProbaO)}
                      {r.ostatnieIp != null && <span className="font-mono text-muted-foreground"> ({r.ostatnieIp})</span>}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">{t('agents.odinstalowania.czeka')}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function Agents() {

  /**
   * WERSJA DOCELOWA — do porównania z wersją na maszynie.
   *
   * ⚠️ TEN SAM KLUCZ CO W `Wdrozenie`, więc react-query oddaje to z pamięci podręcznej i drugiego
   * żądania nie ma. Alternatywą było przewleczenie wartości przez właściwości przez pół ekranu,
   * co przy jednym odczycie jest gorsze niż powtórzone zapytanie z tym samym kluczem.
   */
  const wersjaDocelowa = useQuery({ queryKey: ['installer'], queryFn: installerInfo });
  const inst = wersjaDocelowa.data;

  /**
   * CZY TEN AGENT JEST W TYLE — liczone wobec wersji WYDAWANEJ KANAŁEM, nie wobec paczki instalatora.
   *
   * ⚠️ ZGŁOSZONE Z WDROŻENIA (2026-08-18) I ODWROTNE OD PRAWDY: front porównywał wersję węzła
   * z numerem archiwum instalatora (0.5.97), więc węzeł zaktualizowany do 0.6.96 dostawał strzałkę
   * „powinien się podnieść", a węzeł faktycznie zostający na 0.5.97 nie dostawał jej wcale.
   * Technik miał wtedy dwa ekrany mówiące co innego i żadnego sposobu rozstrzygnięcia.
   *
   * ⚠️ PORÓWNANIE PO CZŁONACH LICZBOWYCH, nie tekstowe: `0.6.9` jest STARSZE niż `0.6.10`, a po
   * znakach wychodzi odwrotnie. I tylko „starsza" zapala znacznik — wersja WYŻSZA od kanału to nie
   * zaległość, a maszyna wyprzedzająca instancję (zdarza się między wdrożeniem obrazu a paczki).
   */
  const wTyle = (wersja?: string | null): boolean => {
    const cel = inst?.channelVersion;
    if (wersja == null || cel == null) return false;
    const a = wersja.split('.').map(Number);
    const b = cel.split('.').map(Number);
    if (a.some(Number.isNaN) || b.some(Number.isNaN)) return false;
    for (let i = 0; i < 3; i += 1) {
      const r = (a[i] ?? 0) - (b[i] ?? 0);
      if (r !== 0) return r < 0;
    }
    return false;
  };
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();
  const { user } = useAuth();
  /** Wiersz w trybie odrzucania: `id` → wpisywany powód. */
  const [odrzucam, setOdrzucam] = useState<{ id: string; powod: string } | null>(null);

  // konta zawężone do klienta nie mają tu wstępu (API pilnuje `SdiOnlyGuard`) — nie rysujemy
  // ekranu, który i tak odmówi
  const isSdi = user?.clientId === null;

  const { data, isPending } = useQuery({
    queryKey: ['agents'],
    queryFn: () => listAgents(),
    enabled: isSdi,
    /**
     * Odświeżanie w tle, bo ten ekran jest otwarty W CZASIE instalacji: technik uruchamia
     * instalator na maszynie i zgłoszenie ma się pojawić samo, bez wciskania F5 po drugiej
     * stronie telefonu.
     */
    refetchInterval: 15_000,
  });

  /**
   * „AKTUALIZUJ TERAZ" — zapisuje prośbę, którą agent usłyszy w odpowiedzi na najbliższy sygnał
   * życia. ⚠️ Nie jest to zdalne uruchomienie aktualizacji i komunikat tego nie obiecuje.
   */
  const aktualizujTeraz = useMutation({
    mutationFn: (agentId: string) => poprosOAktualizacje(agentId),
    onSuccess: (r) => {
      toast.success(t('agents.aktualizujZlecone').replace('{wezel}', r.nodeId));
      void qc.invalidateQueries({ queryKey: ['agents'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const potwierdz = useMutation({
    mutationFn: (id: string) => acceptAgent(id),
    onSuccess: (r) => {
      toast.success(r.changed ? t('agents.accepted') : t('agents.alreadyAccepted'));
      void qc.invalidateQueries({ queryKey: ['agents'] });
      // lista automatów pokazuje plakietkę „czeka na potwierdzenie" — po zmianie stanu jest nieprawdziwa
      void qc.invalidateQueries({ queryKey: ['machines'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  /**
   * ⚠️ ODBLOKOWUJE KANAŁ W DÓŁ, a nie tylko poprawia plakietkę. Config-down w Smart Syncu
   * wstrzymuje WSZYSTKIE operacje dla maszyn, dopóki portal nie wie, który węzeł jest masterem —
   * a wskazanie ustawiało dotąd wyłącznie odtworzenie kopii zlecone z portalu. Gdy master
   * powstał poza naszym zadaniem (tak zrobiła platforma na PESA), nie było czym tego dopiąć.
   */
  const wskazMaster = useMutation({
    mutationFn: ({ groupId, agentId }: { groupId: string; agentId: string }) => wskazMasteraWyspy(groupId, agentId),
    onSuccess: (r) => {
      toast.success(
        r.zmieniono
          ? t('agents.wskazanoMastera').replace('{wezel}', r.nodeId)
          : t('agents.wskazanoMasteraBezZmian'),
      );
      void qc.invalidateQueries({ queryKey: ['agents'] });
      /** Pulpit liczy „silnik synchronizacji" z tego wskazania — bez tego pokazywałby stare. */
      void qc.invalidateQueries({ queryKey: ['overview'] });
      void qc.invalidateQueries({ queryKey: ['przeglad-wysp'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const odrzuc = useMutation({
    mutationFn: ({ id, powod }: { id: string; powod: string }) => rejectAgent(id, powod.trim() || undefined),
    onSuccess: (r) => {
      toast.success(r.changed ? t('agents.rejected') : t('agents.alreadyRejected'));
      setOdrzucam(null);
      void qc.invalidateQueries({ queryKey: ['agents'] });
      void qc.invalidateQueries({ queryKey: ['machines'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  /**
   * ⚠️⚠️ HOOKI NAD BRAMKĄ `isSdi`, NIE POD NIĄ. Konto bez roli SDI wychodziło z komponentu wyżej,
   * więc te trzy hooki wykonywały się tylko części kont — a gdy `isSdi` zmieni się w trakcie
   * życia komponentu (rozstrzygnięcie zapytania o konto, przelogowanie), React przerywa całe
   * drzewo i zakładka gaśnie na biały ekran. Tak wyłożyły się „Moduły" 02.09.2026.
   */
  /**
   * ⚠️ USUWANIE WĘZŁA ZA POTWIERDZENIEM W OKNIE, nie jednym kliknięciem. Odrzucenie da się cofnąć
   * (wystarczy potwierdzić), usunięcia nie — a przyciski stoją w tabeli obok siebie.
   */
  const [doUsuniecia, setDoUsuniecia] = useState<{ id: string; nodeId: string } | null>(null);
  /**
   * ODINSTALOWANIE AGENTA (13.10.2026) — okno z przepisaniem nazwy węzła, także dla potwierdzonego
   * i offline. Węzeł znika od razu, maszyna dostaje polecenie, gdy zapuka (lista „Odinstalowania").
   */
  const [doOdinstalowania, setDoOdinstalowania] = useState<{ id: string; nodeId: string; hostname: string | null } | null>(null);
  const [wpisanyWezel, setWpisanyWezel] = useState('');
  /**
   * OKNO Z POLECENIAMI NAPRAWY SCHEMATU.
   *
   * ⚠️ OSOBNE OKNO, NIE ROZWINIĘCIE W KOMÓRCE: poleceń jest po dwa na tabelę, są długie i są do
   * SKOPIOWANIA — w komórce tabeli by się nie zmieściły ani nie dały zaznaczyć.
   */
  const [naprawaFor, setNaprawaFor] = useState<{
    nodeId: string;
    wyspa: string;
    dane: { tabele: string[]; skutki: string[]; polecenia: string[] };
  } | null>(null);
  /**
   * KTORY WIERSZ MA ROZWINIETE SZCZEGOLY — szesc faktow, na ktorych stoi decyzja o potwierdzeniu.
   *
   * ⚠️ ISTNIEJE, ZEBY SCALENIE LISTY NIE KOSZTOWALO TRESCI. Do 28.08.2026 oczekujacy byl karta
   * i widac bylo przy nim host, role, serwer, baze, VMID i kod wejscia bez klikania. Po scaleniu
   * w tabele te fakty nie moga zniknac — inaczej czlowiek klikalby „Potwierdz", nie widzac tego,
   * co ma ocenic (dokladnie ta obawa stoi w komentarzu na gorze pliku).
   */
  /**
   * ⚠️⚠️ ZBIÓR, NIE JEDEN ID — i to jest warunek działania „rozwiń wszystkie" (zamówione
   * 02.09.2026). Do tej pory stan trzymał JEDEN otwarty wiersz, więc rozwinięcie kolejnego
   * zamykało poprzedni. Przy takim stanie „rozwiń wszystkie" nie dałoby się napisać wcale, a nie
   * tylko wyglądałoby dziwnie.
   *
   * ⚠️ ZMIANA JEST WIDOCZNA TEŻ BEZ TEGO PRZYCISKU: dwa węzły można teraz porównać obok siebie
   * (wersja, baza, strefa czasowa), a właśnie po to się w te szczegóły wchodzi.
   */
  const [szczegoly, setSzczegoly] = useState<ReadonlySet<string>>(() => new Set());
  const przelaczSzczegoly = (id: string): void =>
    setSzczegoly((byly) => {
      const teraz = new Set(byly);
      if (!teraz.delete(id)) teraz.add(id);
      return teraz;
    });
  const usunWezel = useMutation({
    mutationFn: (id: string) => usunAgenta(id),
    onSuccess: (r) => {
      setDoUsuniecia(null);
      toast.success(
        t('agents.deleted')
          .replace('{wezel}', r.nodeId)
          .replace('{tokeny}', String(r.odwolaneTokeny)),
      );
      void qc.invalidateQueries({ queryKey: ['agents'] });
      void qc.invalidateQueries({ queryKey: ['wyspy-przeglad'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const odinstaluj = useMutation({
    mutationFn: ({ id, potwierdzenie }: { id: string; potwierdzenie: string }) => odinstalujAgenta(id, potwierdzenie),
    onSuccess: (r) => {
      setDoOdinstalowania(null);
      setWpisanyWezel('');
      if (r.zleconeTokeny > 0) toast.success(t('agents.odinstaluj.zlecone').replace('{wezel}', r.nodeId));
      else toast.error(t('agents.odinstaluj.bezTokenu').replace('{wezel}', r.nodeId));
      void qc.invalidateQueries({ queryKey: ['agents'] });
      void qc.invalidateQueries({ queryKey: ['machines'] });
      void qc.invalidateQueries({ queryKey: ['wyspy-przeglad'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!isSdi) return null;

  const agenci = data?.agents ?? [];

  const oczekujacy = agenci.filter((a) => a.acceptance === 'OCZEKUJE');
  /**
   * JEDNA LISTA WSZYSTKICH WĘZŁÓW, OCZEKUJĄCY NA WIERZCHU.
   *
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   *  ⚠️⚠️ ZASTĄPIŁO DWIE OSOBNE SEKCJE — zgłoszone (28.08.2026): „«Czekają na potwierdzenie» dodaj
   *  do tej samej listy i po prostu niech tam będzie na żółto i z opcją zatwierdzenia", a cała lista
   *  „leci na samą górę strony, bo to ważne, żeby widzieć, co jest podłączone".
   *
   *  ⚠️ NADPISUJE DECYZJĘ ZAPISANĄ WYŻEJ W TYM PLIKU („oczekujący są kartami, nie wierszami
   *  tabeli"). Obawa, która za nią stała, jest prawdziwa i zaadresowana INACZEJ, nie porzucona:
   *  potwierdzenie ma się opierać na sześciu faktach naraz (host, rola, serwer, baza, VMID, kod
   *  wejścia), więc te fakty NIE zostały streszczone do jednej etykiety — schowały się pod
   *  „Szczegóły" w tym samym wierszu, jedno kliknięcie od przycisku „Potwierdź". Karta pokazywała
   *  je bez klikania, ale kosztem tego, że lista podłączonych agentów była DRUGA na ekranie.
   *
   *  ⚠️ SORTOWANIE, NIE FILTR: oczekujący idą na wierzch, bo tylko oni wymagają czynności. Reszta
   *  zachowuje kolejność z API.
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   */
  const wszyscy = [...oczekujacy, ...agenci.filter((a) => a.acceptance !== 'OCZEKUJE')];
  /**
   * ⚠️ LICZYMY „WSZYSTKIE OTWARTE" Z LISTY, NIE Z ROZMIARU ZBIORU. Węzeł usunięty albo odfiltrowany
   * zostawia swój identyfikator w zbiorze, więc `size === wszyscy.length` potrafiłoby pokazać
   * „zwiń wszystkie" przy zwiniętej liście.
   */
  const wszystkieOtwarte = wszyscy.length > 0 && wszyscy.every((a) => szczegoly.has(a.id));

  const PrzyciskiOdrzucania = ({ a }: { a: AgentRow }) =>
    odrzucam?.id === a.id ? (
      <div className="flex flex-wrap items-center gap-2">
        <Input
          autoFocus
          className="h-8 w-56"
          placeholder={t('agents.reasonHint')}
          value={odrzucam.powod}
          onChange={(e) => setOdrzucam({ id: a.id, powod: e.target.value })}
        />
        <Button
          size="sm"
          variant="destructive"
          disabled={odrzuc.isPending}
          onClick={() => odrzuc.mutate({ id: a.id, powod: odrzucam.powod })}
        >
          {odrzuc.isPending ? <Loader2 className="size-4 animate-spin" /> : <Ban className="size-4" />}
          {t('agents.rejectConfirm')}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setOdrzucam(null)}>
          {t('common.cancel')}
        </Button>
      </div>
    ) : (
      <Button size="sm" variant="outline" onClick={() => setOdrzucam({ id: a.id, powod: '' })}>
        <Ban className="size-4" />
        {t('agents.reject')}
      </Button>
    );

  return (
    <div className="space-y-6">
      {/*
        ⚠️ BALONIK WERSJI PRZY TYTULE, NIE NA PRAWEJ KRAWĘDZI — zgłoszone: „przenieś na lewą stronę
        (między Agenci a «Aktywni agenci»)". Przy szerokim ekranie odsunięty na prawo trafiał metr
        od nazwy, do której się odnosi, i czytało się go jako osobny element, nie jako dopisek.
      */}
      <header className="flex flex-wrap items-baseline gap-2">
        <h1 className="text-lg font-semibold">{t('agents.title')}</h1>
        {/*
          ⚠️⚠️ AKTUALNA WERSJA AGENTA PRZY LISCIE — zgloszone (28.08.2026): „dodaj tylko w jakims
          baloniku na gorze z prawej obecna wersje agenta, zeby bylo od razu widac, co jest aktualne,
          patrzac na liste agentow".

          ⚠️ POKAZUJEMY WERSJE Z KANALU (`channelVersion`), NIE Z PACZKI INSTALATORA. To ona jest
          odpowiedzia na pytanie „co jest aktualne": agent aktualizuje sie sam z kanalu, a wersja
          w archiwum instalatora mowi tylko, z czym startuje SWIEZA instalacja. Tym samym numerem
          liczy sie `wTyle()` przy kazdym wierszu, wiec balonik i strzalki w tabeli nie moga sie
          rozjechac.
        */}
        {inst?.channelVersion ? (
          <Badge variant="neutral" title={t('agents.inst.channel')}>
            {t('agents.aktualnaWersja').replace('{wersja}', inst.channelVersion)}
          </Badge>
        ) : null}
      {/*
        ⚠️ BEZ AKAPITU POD TYTUŁEM — zgłoszone wprost (28.08.2026). Tłumaczył, że potwierdzenie
        węzła jest autoryzacją, a nie formalnością. Ta wiedza nie znika: siedzi teraz PRZY wierszu
        oczekującego węzła (`agents.oczekujeWiersz`), czyli tam, gdzie się nią działa, a nie
        w akapicie nad ekranem, który czyta się raz.
      */}
      </header>

      {isPending && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          {t('common.loading')}
        </p>
      )}


      {/* ── AKTYWNI AGENCI (razem z oczekującymi na potwierdzenie) ────────────── */}
      <section className="space-y-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          {t('agents.othersTitle')}
          {/* Licznik oczekujących zostaje — to jedyna rzecz na tym ekranie, która wymaga czynności. */}
          {oczekujacy.length > 0 && <Badge variant="warning">{oczekujacy.length}</Badge>}
          {/*
            ⚠️⚠️ ROZWIŃ / ZWIŃ WSZYSTKIE — zamówione 02.09.2026 z uzasadnieniem, które jest tu
            najważniejsze: „całkiem zapomniałem, że tam w ogóle to można rozwinąć o dodatkowe
            info". Strzałka «>» przy wierszu jest poprawna, ale mówi „ten wiersz da się rozwinąć"
            dopiero temu, kto już wie, że szczegóły istnieją. Przycisk NAZWANY SŁOWAMI przy
            tytule sekcji mówi to komuś, kto nie wie — i to jest cała jego robota.

            ⚠️ PRZY TYTULE, NIE W GŁÓWCE TABELI: główka przewija się w poziomie razem z tabelą
            (`overflow-x-auto` niżej), więc na wąskim ekranie przycisk mógłby wyjechać za kadr.
          */}
          {wszyscy.length > 0 && (
            <Button
              size="sm"
              variant="ghost"
              className="ml-auto font-normal"
              onClick={() => setSzczegoly(wszystkieOtwarte ? new Set() : new Set(wszyscy.map((a) => a.id)))}
            >
              {wszystkieOtwarte ? <ChevronsDownUp className="size-4" /> : <ChevronsUpDown className="size-4" />}
              {wszystkieOtwarte ? t('agents.zwinWszystkie') : t('agents.rozwinWszystkie')}
            </Button>
          )}
        </h2>
        {!isPending && wszyscy.length === 0 ? (
          <p className="rounded-md border border-border bg-card p-3 text-xs text-muted-foreground">
            {t('agents.othersEmpty')}
          </p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-left text-xs">
              <thead className="bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="p-2 font-medium">{t('agents.colNode')}</th>
                  <th className="p-2 font-medium">{t('agents.colIsland')}</th>
                  <th className="p-2 font-medium">{t('agents.colState')}</th>
                  <th className="p-2 font-medium">{t('agents.colFound')}</th>
                  <th className="p-2 font-medium">{t('agents.colLastSeen')}</th>
                  <th className="p-2 font-medium">{t('agents.colDecision')}</th>
                  <th className="p-2" />
                </tr>
              </thead>
              <tbody>
                {wszyscy.map((a) => {
                  const oczekuje = a.acceptance === 'OCZEKUJE';
                  const otwarte = szczegoly.has(a.id);
                  return (
                  <Fragment key={a.id}>
                  {/*
                    ⚠️ ZOLTE TLO CALEGO WIERSZA, nie sama plakietka w kolumnie stanu — zgloszone:
                    „niech tam bedzie na zolto i z opcja zatwierdzenia". Wiersz do potwierdzenia
                    musi dac sie znalezc wzrokiem na liscie kilkudziesieciu, a plakietka w trzeciej
                    kolumnie tego nie robi.
                  */}
                  <tr className={cn('border-t border-border align-top', oczekuje && 'bg-[var(--warning)]/10')}>
                    <td className="p-2">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {/* Rozwiniecie szczegolow — patrz `szczegoly`. */}
                        <button
                          type="button"
                          onClick={() => przelaczSzczegoly(a.id)}
                          aria-expanded={otwarte}
                          title={t('agents.szczegoly')}
                          className="rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <ChevronRight className={cn('size-3.5 transition-transform', otwarte && 'rotate-90')} aria-hidden />
                        </button>
                        {/*
                          ⚠️⚠️ SZAFA KONTRA CHMURKA — zamówione 02.09.2026: „dodaj wyraźne ikonki
                          szafy vendingowej i chmurki, żeby było od razu wzrokowo wiadomo, że to
                          cloud agent, a tamte na maszynie".

                          ⚠️ ROZSTRZYGA BRAK AUTOMATU, NIE `kind`. Węzeł chmurowy ma `kind`
                          `DATABASE` DOKŁADNIE TAK SAMO jak maszyna z bazą — dlatego stara ikonka
                          (baza kontra monitor) pokazywała to samo dla chmury i dla pięciu maszyn
                          hostujących bazę. Tożsamość węzła chmurowego to „nie ma automatu i nie ma
                          VMID" (ta sama reguła, którą liczy `chmuraNodeId` po stronie API).

                          ⚠️ IKONA MA DYMEK: kształt mówi „gdzie to stoi" dopiero, gdy raz się go
                          nauczy, a dymek nie kosztuje nic i uczy przy pierwszym najechaniu.
                        */}
                        {/** ⚠️ DYMEK NA OPAKOWANIU: ikony lucide nie przyjmują `title`, a `<title>` w SVG nie jest czytane spójnie. */}
                        <span
                          title={a.machine === null && a.spsVmid === null ? t('agents.ikonaChmura') : t('agents.ikonaMaszyna')}
                          aria-label={a.machine === null && a.spsVmid === null ? t('agents.ikonaChmura') : t('agents.ikonaMaszyna')}
                          className="inline-flex shrink-0"
                        >
                          {a.machine === null && a.spsVmid === null ? (
                            <Cloud className="size-4 text-[var(--primary)]" aria-hidden />
                          ) : (
                            <Refrigerator className="size-4" aria-hidden />
                          )}
                        </span>
                        <span className="font-mono">{a.nodeId}</span>
                        {/*
                          ⚠️⚠️ KTÓRY WĘZEŁ JEST MASTEREM — ODPOWIEDŹ, KTÓREJ DO DZIŚ NIE BYŁO NIGDZIE.
                          Nazwa bazy master stoi w Ustawieniach, ale w trybie `SMART_SYNC` KAŻDA
                          maszyna ma bazę o tej samej nazwie, więc z listy nie dało się wyczytać,
                          gdzie master NAPRAWDĘ stoi. Zgłoszone przez użytkownika 25.08.2026, po
                          zasianiu chmurowego mastera: „master musi być widoczny gdzieś w portalu
                          jako baza i że poprawnie się synchronizuje".

                          ⚠️ Wskazanie bierze się z `masterAgentId` wyspy, które ustawia ZASIANIE
                          (udane odtworzenie bazy o nazwie mastera na węźle bez automatu) — a nie
                          z porównania nazw, bo ono wskazywałoby wszystkie węzły naraz.
                        */}
                        {a.group.masterAgentId === a.id && (
                          <Badge variant="default" title={t('agents.masterWyspyOpis')}>
                            {t('agents.masterWyspy')}
                          </Badge>
                        )}

                        {/*
                          ⚠️⚠️ ŚLEDZENIE ZMIAN WIDOCZNE BEZ ROZWIJANIA — zamówione 03.09.2026:
                          „teraz to trzeba rozwijać, żeby znać status". Przy kilkudziesięciu
                          węzłach rozwijanie każdego po kolei, żeby ustalić, gdzie coś zjada bazę,
                          jest właśnie tą robotą, której ten portal ma oszczędzać.

                          ⚠️ DWA RÓŻNE KOLORY, BO DWA RÓŻNE KOSZTY. `SCT` (replikacja SupplyPointa)
                          jest czerwone: to ono u klienta miało 471 MB, 3,5 mln wierszy i kursor
                          blokujący JEDEN wiersz przy każdym wydaniu. `CT` (natywny Change
                          Tracking) jest żółte: kosztuje, ale nie kolejkuje zapisów.

                          ⚠️ POKAZUJEMY TYLKO WŁĄCZONE — też z zamówienia („jak nie będą włączone
                          to nie wyświetlaj"). Plakietka „SCT wyłączony" przy każdym zdrowym węźle
                          zamieniłaby listę w szum, w którym te dwa realne przypadki znikają.

                          ⚠️ WARUNEK DLA `SCT` TO `jestCoWylaczac`, NIE „są tabele": pusty
                          `SyncChangeTracking` bez triggerów nic nie kosztuje i nie ma o czym
                          krzyczeć. Ten sam warunek wyszarza przycisk w rozwinięciu, więc
                          plakietka i przycisk nie mogą się rozjechać.
                        */}
                        {a.sledzenie?.spsSync?.jestCoWylaczac === true && (
                          <Badge variant="destructive" title={t('agents.sctOpis')}>
                            {t('agents.sct')}
                          </Badge>
                        )}
                        {a.sledzenieZmian?.ct.wlaczony === true && (
                          <Badge variant="warning" title={t('agents.ctOpis')}>
                            {t('agents.ct')}
                          </Badge>
                        )}
                        {/*
                          ⚠️⚠️ WSKAZANIE RĘKĄ — DZIURA ZNALEZIONA NA WDROŻENIU PESA (28.08.2026).
                          `masterAgentId` ustawiało WYŁĄCZNIE odtworzenie kopii zlecone z portalu.
                          Platforma odtworzyła bazę mastera po swojej stronie, więc wskazanie nie
                          miało jak powstać — a config-down w `SMART_SYNC` bez niego WSTRZYMUJE
                          wszystkie operacje dla maszyn. Wyspa z gotowym masterem stała, a jedynym
                          wyjściem było skasowanie świeżo odtworzonej bazy produkcyjnej.

                          ⚠️ POKAZUJEMY WYŁĄCZNIE TAM, GDZIE WOLNO: wyspa w Smart Syncu, węzeł
                          potwierdzony, rodzaju `DATABASE` i BEZ automatu. Ostatni warunek jest
                          najważniejszy — w tym trybie każda maszyna ma bazę o tej samej nazwie co
                          master, więc bez niego dałoby się wskazać automat. Regułę trzyma API
                          (`powodOdmowyWskazania`), tu jest tylko jej odbicie na ekranie.
                        */}
                        {a.group.masterAgentId !== a.id &&
                          a.group.tryb === 'SMART_SYNC' &&
                          a.kind === 'DATABASE' &&
                          a.machine === null &&
                          /**
                           * ⚠️⚠️ VMID ODCINA MASZYNĘ ŚWIEŻO PO REJESTRACJI. Zgłoszone 01.09.2026
                           * z instancji postawionej od zera: przy PIERWSZYM agencie maszyny stał
                           * przycisk „Wskaż jako master wyspy", bo automat dowiązuje się dopiero
                           * po pierwszej synchronizacji konfiguracji. Regułę trzyma API
                           * (`powodOdmowyWskazania`), tu jest jej odbicie.
                           */
                          a.spsVmid === null &&
                          a.acceptance === 'POTWIERDZONY' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-6"
                              disabled={wskazMaster.isPending}
                              title={t('agents.wskazMasteraOpis')}
                              onClick={() => wskazMaster.mutate({ groupId: a.group.id, agentId: a.id })}
                            >
                              {wskazMaster.isPending ? (
                                <Loader2 className="size-3 animate-spin" />
                              ) : (
                                <Database className="size-3" />
                              )}
                              {t('agents.wskazMastera')}
                            </Button>
                          )}
                      </div>
                      <span className="text-muted-foreground">{a.hostname ?? '—'}</span>
                      {/*
                        ⚠️ WERSJA AGENTA NA LIŚCIE, nie tylko w karcie oczekującego. Zgłoszone
                        wprost: „nigdzie nie ma tego". Agent POTWIERDZONY — czyli każdy pracujący —
                        pokazywał wersję wyłącznie w sekcji „do potwierdzenia", więc pytanie „czy ta
                        maszyna ma już nowe wydanie" nie miało gdzie znaleźć odpowiedzi. To ta sama
                        pomyłka, którą wcześniej naprawił zegar w tej samej tabeli.

                        ⚠️ POKAZUJEMY TEŻ, ŻE JEST STARSZA NIŻ DOCELOWA. Sam numer nie odpowiada na
                        pytanie, które się przy nim zadaje: agent aktualizuje się sam, ale dopiero
                        gdy zapyta o wersję (co kilkadziesiąt cykli), a maszyna wyłączona na noc
                        zostaje w tyle bez żadnego objawu.
                      */}
                      <br />
                      {a.version === null || a.version === undefined ? (
                        <span className="font-mono text-[11px] text-muted-foreground">
                          {t('agents.verShort')} —
                        </span>
                      ) : (
                        <span
                          className={cn('font-mono text-[11px]', wTyle(a.version) ? 'text-[var(--warning)]' : 'text-muted-foreground')}
                          title={
                            wTyle(a.version)
                              ? t('agents.verBehind').replace('{docelowa}', String(inst?.channelVersion))
                              : t('agents.fVersion')
                          }
                        >
                          {t('agents.verShort')} {a.version}
                          {wTyle(a.version) ? ' ↑' : ''}
                        </span>
                      )}
                      {/*
                        ⚠️⚠️ PRZYCISK POKAZUJEMY WYŁĄCZNIE, GDY AGENT JEST W TYLE. Kiedy chodzi na
                        wersji z instancji, prośba nie miałaby czego pobrać — a przycisk, który
                        „nic nie robi i tak ma być", uczy ludzi, że przyciski tu nie działają.

                        ⚠️ WYŁĄCZONY Z POWODEM, NIE UKRYTY, gdy agent jest za stary, żeby prośbę
                        zrozumieć. Ukrycie znaczyłoby, że technik szuka funkcji, o której nie wie,
                        czy istnieje; wyłączony przycisk mówi wprost, że ta maszyna podniesie się
                        sama. Zaraz po wydaniu tej funkcji to jest stan NORMALNY na całej flocie.
                      */}
                      {wTyle(a.version) && (
                        <>
                          <br />
                          <button
                            type="button"
                            disabled={!umieProsbeOAktualizacje(a.version) || aktualizujTeraz.isPending}
                            onClick={() => aktualizujTeraz.mutate(a.id)}
                            title={
                              umieProsbeOAktualizacje(a.version)
                                ? t('agents.aktualizujTerazOpis')
                                : t('agents.aktualizujTerazStary').replace(
                                    '{wersja}',
                                    WERSJA_Z_AKTUALIZACJA_NA_ZADANIE,
                                  )
                            }
                            className="mt-0.5 text-[11px] underline decoration-dotted hover:text-foreground disabled:cursor-not-allowed disabled:no-underline disabled:opacity-50"
                          >
                            {t('agents.aktualizujTeraz')}
                          </button>
                        </>
                      )}
                    </td>
                    <td className="p-2">
                      {a.group.client.name}
                      <br />
                      <span className="text-muted-foreground">{a.group.name}</span>
                    </td>
                    <td className="p-2">
                      <StanBadge stan={a.acceptance} />
                      {/*
                        ⚠️ ZDANIE, KTORE BYLO AKAPITEM NAD CALYM EKRANEM. Do 28.08.2026 nagłówek
                        strony tłumaczył, że potwierdzenie jest autoryzacją, a portal przyjmuje od
                        niepotwierdzonego węzła wyłącznie sygnał życia. Akapit nad ekranem czyta się
                        raz; przy wierszu, w którym się właśnie klika, czyta się wtedy, kiedy trzeba.
                      */}
                      {oczekuje && (
                        <p className="mt-1 max-w-56 text-[11px] text-[var(--warning)]">{t('agents.oczekujeWiersz')}</p>
                      )}
                      {/**
                       * Problem poprzedniego cyklu pokazujemy TU, obok stanu: agent potwierdzony,
                       * który melduje się co minutę i nic nie robi, jest inaczej zepsuty niż agent
                       * milczący, a bez tego pola oba wyglądają na zdrowe.
                       */}
                      {a.lastProblem !== null && (
                        <p className="mt-1 max-w-64 text-[11px] text-[var(--warning)]">{a.lastProblem}</p>
                      )}
                      {/**
                       * UWAGI Z CYKLU — spokojniejszym kolorem niż problem, bo to NIE awaria.
                       *
                       * ⚠️⚠️ TO POLE ROZWIĄZAŁOBY DZISIEJSZĄ AWARIĘ SAMO (testówka PESA,
                       * 26.08.2026): agent zgłaszał „wydania rodzeństwa: portal nie odpowiedział"
                       * w każdym cyklu, a zdanie nie opuszczało dysku maszyny klienta.
                       *
                       * ⚠️ TRZY PIERWSZE I LICZNIK, nie wszystkie: uwagi powstają w pętli po
                       * tabelach, więc bywa ich kilka — pełna lista rozjechałaby wiersz tabeli.
                       */}
                      {(a.uwagi ?? []).length > 0 && (
                        <div className="mt-1 max-w-64 space-y-0.5">
                          {(a.uwagi ?? []).slice(0, 3).map((u) => (
                            <p key={u} className="text-[11px] leading-snug text-muted-foreground">
                              {u}
                            </p>
                          ))}
                          {(a.uwagi ?? []).length > 3 && (
                            <p className="text-[11px] text-muted-foreground">
                              {t('agents.uwagiWiecej').replace('{ile}', String((a.uwagi ?? []).length - 3))}
                            </p>
                          )}
                        </div>
                      )}
                      {/*
                        ════════════════════════════════════════════════════
                         TABELE BEZ KLUCZA GŁÓWNEGO — CO TO ZABIERA I JAK TO NAPRAWIĆ.

                         ⚠️⚠️ POWSTAŁO Z POMIARU NA PRODUKCJI TELEPLAN/RECONEXT (04.09.2026). Agent
                         meldował „pominięto Drawers: brak klucza głównego" OD STYCZNIA i ten ekran
                         rysował to jako jedną z uwag — trzy pierwsze i licznik „+2 więcej". Nigdzie
                         nie stało, że SKUTKIEM jest brak planogramu wyspy, ani że config-down
                         ODMÓWI zapisu geometrii. Pomiar był, znaczenia nie było.

                         ⚠️⚠️ OSOBNO OD UWAG, NIE JAKO KOLEJNA UWAGA — i to jest sedno tej zmiany.
                         Uwaga to zdanie do przeczytania; to jest STAN, który trzeba naprawić,
                         i dlatego ma nazwę, skutek i polecenia do skopiowania. Plakietka, z której
                         nie da się zrobić kroku, uczy patrzącego, że można ją ignorować.

                         ⚠️ POLECENIA SĄ Z SERWERA, nie składane tutaj: wymagają kluczy naturalnych tabel
                         SPS. Portal ich NIE WYKONUJE — `ALTER TABLE` na produkcyjnej bazie klienta
                         jest poza tym, co powinno dziać się bez człowieka.
                        ════════════════════════════════════════════════════
                      */}
                      {a.bezKlucza != null && a.bezKlucza.tabele.length > 0 && (
                        <div className="mt-1.5 max-w-64 rounded-md border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-2">
                          <p className="flex items-start gap-1.5 text-[11px] font-medium">
                            <KeyRound className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
                            {t('agents.bezKlucza')
                              .replace('{ile}', String(a.bezKlucza.tabele.length))}
                          </p>
                          {/* ⚠️ SKUTEK PRZED TABELAMI: „brak planogramu" znaczy coś dla patrzącego, nazwa tabeli nie. */}
                          {a.bezKlucza.skutki.length > 0 && (
                            <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
                              {t('agents.bezKluczaSkutek').replace('{co}', a.bezKlucza.skutki.join(', '))}
                            </p>
                          )}
                          <p className="mt-0.5 font-mono text-[10px] leading-snug text-muted-foreground">
                            {a.bezKlucza.tabele.join(', ')}
                          </p>
                          <button
                            type="button"
                            className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-[var(--primary)] hover:underline"
                            onClick={() => setNaprawaFor({ nodeId: a.nodeId, wyspa: a.group.name, dane: a.bezKlucza! })}
                          >
                            <Wrench className="size-3" aria-hidden="true" />
                            {t('agents.bezKluczaPokazNaprawe')}
                          </button>
                        </div>
                      )}
                      {/**
                       * STAN KANAŁU WYDAŃ RODZEŃSTWA — tutaj, obok stanu węzła, bo to pytanie
                       * o ZDROWIE, nie o konfigurację.
                       *
                       * ⚠️⚠️ TEN BLOK POWSTAŁ ZE ZMIERZONEJ AWARII (testówka PESA, 26.08.2026):
                       * kanał nie wpisał ANI JEDNEGO wiersza przez pół dnia, a ten ekran pokazywał
                       * cztery zdrowe węzły — bo o kanale nie mówił NICZEGO. Diagnoza wymagała
                       * czytania licznika ruchu instancji i źródeł z dysków maszyn.
                       *
                       * ⚠️ KOLEJNOŚĆ WARUNKÓW JEST TREŚCIĄ: najpierw błąd (najpoważniejszy), potem
                       * „nie pyta" (dzisiejsza awaria), potem zaległość, na końcu norma. Odwrotna
                       * kolejność pokazałaby „nadąża" węzłowi, który w ogóle nie wykonuje kroku.
                       */}
                      {(() => {
                        /** Na wyspie bez rozsyłania kanał MA milczeć — zdanie o nim byłoby szumem. */
                        if (a.group.tryb !== 'SMART_SYNC' || a.group.wydaniaRodzenstwa !== true) return null;

                        const blad = a.wydaniaDolBlad ?? '';
                        if (blad !== '') {
                          return (
                            <p className="mt-1 max-w-64 text-[11px] text-destructive">
                              {t('agents.rodz.blad').replace('{blad}', blad)}
                            </p>
                          );
                        }

                        const pytal = a.wydaniaDolPytalO ?? null;
                        /**
                         * ⚠️ PRÓG 5 MINUT, NIE JEDNA. Cykl agenta ma ~60 s z rozrzutem ±15 %, a po
                         * nieudanym cyklu odstęp narasta — jedna minuta dawałaby ostrzeżenie
                         * przy każdym normalnym zawahaniu i nauczyłaby wszystkich je ignorować.
                         */
                        const minut = pytal === null ? null : Math.floor((Date.now() - new Date(pytal).getTime()) / 60000);
                        if (minut === null || minut > 5) {
                          return (
                            <p className="mt-1 max-w-64 text-[11px] text-[var(--warning)]">
                              {t('agents.rodz.milczy').replace('{kiedy}', minut === null ? '—' : `${minut} min`)}
                            </p>
                          );
                        }

                        const kursor = a.wydaniaDolSeq ?? null;
                        /**
                         * ⚠️ `wydaniaDolSzczyt` TO SZCZYT WYDAŃ **OBCYCH** DLA TEGO WĘZŁA, nie
                         * szczyt wyspy — i to jest poprawka po błędzie zobaczonym na wdrożeniu
                         * (26.08.2026): automat, który sam wydał ostatni wiersz, pokazywał
                         * „1 czeka" na zawsze, bo jego własne wydanie jest z jego paczki
                         * wykluczone i kursor nie przejdzie go nigdy.
                         *
                         * ⚠️ KURSOR `null` TO NIE ZALEGŁOŚĆ: mechanizm nie był tu włączany, więc
                         * pierwsze pytanie ustawi kursor na szczyt i nic nie dociągnie.
                         */
                        const szczyt = a.wydaniaDolSzczyt ?? 0;
                        /**
                         * ⚠️⚠️ DWA RÓŻNE STANY, KTÓRE DO 02.09.2026 WYGLĄDAŁY IDENTYCZNIE:
                         *
                         *   • DOCIĄGANIE HISTORII — nowe wydania jadą normalnie, a zaległość
                         *     schodzi w przerwach (granica żywych ustawiona). Trzy godziny takiego
                         *     stanu to norma po przewinięciu i NIE jest awarią.
                         *   • ZALEGŁOŚĆ ZWYKŁA — kanał nie nadąża z bieżącymi wydaniami.
                         *
                         * Zgłoszone wprost: „od 3h jak to się robi, to na innych maszynach nie ma
                         * synchro nowych wydań" — ekran nie mówił, że to dociąganie, więc każdy
                         * czytał to jak zator.
                         */
                        const zyweOd = a.wydaniaZyweOd ?? null;
                        if (kursor !== null && zyweOd !== null && zyweOd > kursor) {
                          return (
                            <p className="mt-1 max-w-64 text-[11px] text-muted-foreground">
                              {/*
                                ⚠️ BEZ „ZOSTAŁO N WIERSZY". `granica − kursor` to odległość
                                w NUMERACH, a nie liczba wierszy: numery mają dziury, a z paczki
                                tego węzła wypadają jeszcze jego własne wydania. Podana jako
                                liczba wierszy byłaby zmyśloną precyzją.
                              */}
                              {t('agents.rodz.dociaganie')
                                .replace('{kursor}', String(kursor))
                                .replace('{granica}', String(zyweOd))}
                            </p>
                          );
                        }
                        if (kursor !== null && szczyt > kursor) {
                          return (
                            <p className="mt-1 max-w-64 text-[11px] text-[var(--warning)]">
                              {t('agents.rodz.zaleglosc')
                                .replace('{kursor}', String(kursor))
                                .replace('{szczyt}', String(szczyt))}
                            </p>
                          );
                        }
                        /**
                         * ⚠️ LICZNIK TYLKO GDY NIEZEROWY. Kolumna jest nowa, więc wiersze wpisane
                         * przed jej dodaniem się w niej nie liczą — „nadąża (0 wpisanych)" zaraz
                         * po udanym wpisie czyta się jak awaria, a nie jak norma.
                         */
                        const wpisanych = a.wydaniaDolWpisanych ?? 0;
                        return (
                          <p className="mt-1 max-w-64 text-[11px] text-muted-foreground">
                            {wpisanych > 0
                              ? t('agents.rodz.nadaza').replace('{wpisanych}', String(wpisanych))
                              : t('agents.rodz.nadazaProsto')}
                          </p>
                        );
                      })()}
                      {/*
                        ⚠️⚠️ ZALEGŁOŚĆ KONFIGURACJI TEGO WĘZŁA — ze zmierzonej awarii (IKEA, 23.09.2026):
                        „coraz więcej operacji wisi, chociaż wszyscy agenci są online". Węzeł po poczekalni
                        przerabiał zaległość, każda nowa operacja wyspy czekała na niego, a ten wiersz
                        wyglądał zdrowo. Tylko gdy niezerowa — przy zdrowym węźle byłaby szumem.
                      */}
                      {(a.doPrzerobienia ?? 0) > 0 && (
                        <p className="mt-1 max-w-64 text-[11px] text-muted-foreground">
                          {t('agents.doPrzerobienia').replace('{n}', String(a.doPrzerobienia))}
                        </p>
                      )}
                    </td>
                    <td className="p-2 font-mono text-[11px]">
                      {(() => {
                        /**
                         * ⚠️ ROZJAZD BAZY W TABELI, nie tylko w rozwiniętej karcie. To jedyna
                         * kolumna, w której widać, SKĄD są dane tej maszyny — a przy rozjeździe
                         * cała reszta wiersza mówi o innej bazie, niż maszyna naprawdę używa.
                         */
                        const b = opisBazy(a.spsDatabase, a.group.masterDatabase, { tryb: a.group.tryb, tylkoBaza: a.machine === null });
                        return b.waga === 'uwaga' ? (
                          <span className="text-[var(--warning)]" title={b.powod ?? undefined}>
                            ⚠ {b.tekst} <span className="text-muted-foreground">≠ {a.group.masterDatabase}</span>
                          </span>
                        ) : (
                          (a.spsDatabase ?? '—')
                        );
                      })()}
                      <br />
                      <span className="text-muted-foreground">
                        {a.nodeRole ?? '—'}
                        {/*
                          ⚠️⚠️ „BEZ AUTOMATU" TO ODPOWIEDŹ, A NIE BRAK ODPOWIEDZI. Węzeł chmurowego
                          mastera jest samą bazą i `VMID` mieć nie będzie — ustalone z użytkownikiem
                          25.08.2026: „VMID nie będzie miał, bo nie ma swojego automatu, jest tylko
                          bazą". Pusta kratka w tym miejscu czytała się jak niedokończone wdrożenie
                          i kazała szukać brakującego numeru, którego nie ma i nie będzie.
                        */}
                        {a.spsVmid !== null
                          ? ` · VMID ${a.spsVmid}`
                          : a.machine === null
                            ? ` · ${t('agents.bezAutomatu')}`
                            : ''}
                        {a.machineIp ? ` · ${a.machineIp}` : ''}
                      </span>
                    </td>
                    <td className="p-2">
                      {when(a.lastSeenAt)}
                      {/**
                       * ⚠️ ZEGAR MUSI BYĆ TU, A NIE TYLKO NA KARCIE OCZEKUJĄCEGO. Fakty o maszynie
                       * renderowały się wyłącznie w sekcji „do potwierdzenia", więc agent POTWIERDZONY
                       * — czyli każdy działający — nie pokazywał rozjazdu zegara nigdzie. Zgłoszone
                       * przez użytkownika przy maszynie, która ma realnie zły czas: dane docierały
                       * do bazy i do API, a ekran ich nie miał. Sprawdzenie „czy przyszło" nie jest
                       * sprawdzeniem „czy widać".
                       */}
                      {(() => {
                        const z = opisRozjazdu(a.clockSkewSeconds);
                        const s = opisStrefy(a.timeZone, a.group.timeZone);
                        if (z.waga === 'nieznane' && s.waga === 'nieznane') return null;
                        return (
                          <div className="mt-1 text-[11px] leading-snug">
                            <span
                              className={z.waga === 'uwaga' ? 'text-[var(--warning)]' : 'text-muted-foreground'}
                              title={t('agents.fClock')}
                            >
                              {t('agents.clockShort')} {z.tekst}
                              {/*
                                ⚠️ PRZY ROZJEŹDZIE MÓWIMY, CZY KTOŚ GO NAPRAWI. Sam rozjazd nie
                                odpowiada na pytanie, które się przy nim zadaje — „czy to samo
                                zniknie". Korekta jest ustawieniem WYSPY i domyślnie wyłączonym,
                                więc bez tego zdania liczba świeci, a nikt nie wie, czy czekać.
                              */}
                              {z.waga === 'uwaga' && (
                                <span className="text-muted-foreground">
                                  {' · '}
                                  {a.group.zegarNaprawiaj === true
                                    ? t('agents.clockFixOn')
                                    : t('agents.clockFixOff')}
                                </span>
                              )}
                            </span>
                            {(s.waga === 'uwaga' || strefaNietypowa(a.timeZone)) && (
                              <>
                                <br />
                                <span
                                  className={s.waga === 'uwaga' ? 'text-[var(--warning)]' : 'text-muted-foreground'}
                                  title={s.powod ?? t('agents.fTimeZone')}
                                >
                                  {s.tekst}
                                </span>
                              </>
                            )}
                            {/**
                             * ⚠️ POKAZUJEMY TYLKO, GDY COŚ JEST NIE TAK. Działająca usługa czasu
                             * przy zgodnym zegarze nie jest informacją — jest tłem. Wypisywanie
                             * jej przy każdym z kilkudziesięciu agentów zagłuszyłoby te dwa
                             * wiersze, w których naprawdę trzeba coś zrobić.
                             *
                             * ⚠️ ETYKIETA MÓWI „SYNCHRONIZACJA ZEGARA", nie „synchronizacja" —
                             * i to jest poprawka po pomyłce, którą ten skrót naprawdę wywołał
                             * (2026-08-18). To stan usługi czasu Windows (`W32Time`), a przy
                             * węźle w tabeli agentów samo „synchronizacja: ręczny, zatrzymana"
                             * czyta się jako „nie synchronizują się DANE z bazy wyspy" — czyli
                             * alarm o awarii, której nie ma.
                             */}
                            {(() => {
                              const sy = opisSynchronizacji(a.timeSyncStart, a.timeSyncRunning);
                              if (sy.waga !== 'uwaga') return null;
                              return (
                                <>
                                  <br />
                                  <span className="text-[var(--warning)]" title={sy.powod ?? undefined}>
                                    {t('agents.timeSyncShort')} {sy.tekst}
                                  </span>
                                </>
                              );
                            })()}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="p-2 text-[11px]">
                      {a.acceptance === 'ODRZUCONY' ? (
                        <>
                          {when(a.rejectedAt)}
                          <br />
                          <span className="text-muted-foreground">{a.rejectedByEmail ?? '—'}</span>
                          {a.rejectReason !== null && <p className="text-muted-foreground">„{a.rejectReason}"</p>}
                        </>
                      ) : (
                        <>
                          {when(a.acceptedAt)}
                          <br />
                          <span className="text-muted-foreground">{a.acceptedByEmail ?? '—'}</span>
                        </>
                      )}
                    </td>
                    <td className="p-2">
                      {a.acceptance === 'POTWIERDZONY' ? (
                        <div className="space-y-2">
                          <PrzyciskiOdrzucania a={a} />
                          <Button
                            size="sm"
                            variant="ghost"
                            title={t('agents.odinstaluj.hint')}
                            onClick={() => setDoOdinstalowania({ id: a.id, nodeId: a.nodeId, hostname: a.hostname })}
                          >
                            <PackageX className="size-4" />
                            {t('agents.odinstaluj.przycisk')}
                          </Button>
                          {/*
                            ⚠️ TYLKO WĘZEŁ Z DOSTĘPEM DO BAZY. `BACKUP DATABASE` wykonuje serwer
                            SQL, a węzeł stojący przy automacie (`MACHINE`) nie ma do niego hasła —
                            przycisk u niego byłby obietnicą, która kończy się błędem po minucie.
                          */}
                        </div>
                      ) : (
                        <div className="flex flex-wrap items-center gap-2">
                          <Button size="sm" variant="outline" disabled={potwierdz.isPending} onClick={() => potwierdz.mutate(a.id)}>
                            <ShieldCheck className="size-4" />
                            {t('agents.accept')}
                          </Button>
                          {/**
                            * ⚠️ USUWANIE TYLKO PRZY WĘŹLE NIEPOTWIERDZONYM — instancja odmawia tego
                            * samego, ale przycisk, którego nie ma, nie wymaga tłumaczenia. Sprząta
                            * po nieudanej rejestracji: wiersz znika, token zostaje odwołany, a odpis
                            * (węzeł, maszyna, baza, kto odrzucił) jedzie do audytu.
                            */}
                          <Button
                            size="sm"
                            variant="ghost"
                            title={t('agents.deleteHint')}
                            disabled={usunWezel.isPending}
                            onClick={() => setDoUsuniecia({ id: a.id, nodeId: a.nodeId })}
                          >
                            <Trash2 className="size-4" />
                            {t('agents.delete')}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            title={t('agents.odinstaluj.hint')}
                            onClick={() => setDoOdinstalowania({ id: a.id, nodeId: a.nodeId, hostname: a.hostname })}
                          >
                            <PackageX className="size-4" />
                            {t('agents.odinstaluj.przycisk')}
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                  {otwarte && (
                    <tr className={cn('border-t border-border', oczekuje && 'bg-[var(--warning)]/10')}>
                      <td className="p-2 pl-8" colSpan={7}>
                        {/*
                          ⚠️ TE SAME FAKTY, CO NA DAWNEJ KARCIE OCZEKUJACEGO, i to jest caly powod
                          istnienia tego wiersza. Lista jest teraz jedna, ale decyzja o potwierdzeniu
                          nadal opiera sie na szesciu wartosciach naraz.
                        */}
                        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3 lg:grid-cols-4">
                          <Fakt etykieta={t('agents.fHostname')} wartosc={a.hostname} />
                          {/**
                           * ⚠️ Adres z OSTATNIEGO meldunku, nie z instalacji. Automaty stoja na DHCP,
                           * wiec adres sprzed pol roku prowadzi w zle miejsce — a to jest pole, po ktore
                           * siega sie dokladnie wtedy, gdy trzeba sie na maszyne wpiac.
                           */}
                          <Fakt etykieta={t('agents.fIp')} wartosc={a.machineIp ?? null} />
                          <Fakt etykieta={t('agents.fRole')} wartosc={a.nodeRole} />
                          <Fakt etykieta={t('agents.fVmid')} wartosc={a.spsVmid} />
                          <Fakt etykieta={t('agents.fServer')} wartosc={a.spsServer} />
                          <FaktZOcena
                            etykieta={t('agents.fDatabase')}
                            opis={opisBazy(a.spsDatabase, a.group.masterDatabase, { tryb: a.group.tryb, tylkoBaza: a.machine === null })}
                          />
                          <Fakt etykieta={t('agents.fVersion')} wartosc={a.version} />
                          <Fakt etykieta={t('agents.fEnrolled')} wartosc={when(a.enrolledAt)} />
                          <FaktZOcena etykieta={t('agents.fClock')} opis={opisRozjazdu(a.clockSkewSeconds)} />
                          <FaktZOcena
                            etykieta={t('agents.fTimeZone')}
                            opis={opisStrefy(a.timeZone, a.group.timeZone)}
                            wyroznij={strefaNietypowa(a.timeZone)}
                          />
                          <FaktZOcena
                            etykieta={t('agents.fTimeSync')}
                            opis={opisSynchronizacji(a.timeSyncStart, a.timeSyncRunning)}
                          />
                          <Fakt
                            etykieta={t('agents.fCode')}
                            wartosc={a.enrollment ? `${a.enrollment.label} (${a.enrollment.prefix}…)` : null}
                          />
                        </dl>
                        {/*
                          ŚLEDZENIE ZMIAN W BAZIE TEGO WĘZŁA — zamówione 02.09.2026.

                          ⚠️ PRZY WĘŹLE, NIE PRZY WYSPIE. Change Tracking włącza się na KONKRETNEJ
                          bazie, a w Smart Syncu każda maszyna ma własną — jedna liczba na wyspę
                          zlałaby maszynę, na której to chodzi, z tymi, na których nie.
                        */}
                        <div className="mt-3 border-t border-border pt-2">
                          <p className="mb-1 text-xs font-medium">{t('sledz.tytul')}</p>
                          <SledzenieBazy
                            podsumowanie={a.sledzenie ?? null}
                            surowe={a.sledzenieZmian ?? null}
                            zmierzono={a.sledzenieZmianAt ?? null}
                            nodeId={a.nodeId}
                            agentId={a.id}
                            groupId={a.group.id}
                            wylaczenie={a.wylaczenieSpsSync ?? null}
                            wylaczenieCt={a.wylaczenieCt ?? null}
                          />
                        </div>
                        {a.enrollment !== null && a.enrollment.revokedAt === null && oczekuje && (
                          /**
                           * Przypomnienie o kodzie stoi PRZY podejrzanym zgloszeniu, nie na osobnym
                           * ekranie: odrzucenie blokuje ten jeden wezel, a kod nadal wpuszcza kolejne.
                           */
                          <p className="mt-2 text-[11px] text-muted-foreground">{t('agents.codeStillActive')}</p>
                        )}
                      </td>
                    </tr>
                  )}
                  </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <OdwolaneProbujace />

      <Odinstalowania />

      {/*
        ⚠️ KOLEJNOSC EKRANU JEST TERAZ ODWROTNA NIZ DO 28.08.2026 — zgloszone wprost: lista agentow
        „leci na sama gore strony, bo to wazne, zeby widziec, co jest podlaczone", chmurowy master
        pod nia, a kod i instalator na koniec.

        ⚠️ TO NIE JEST KOSMETYKA, TYLKO ZMIANA ADRESATA EKRANU. Kod i instalator sa potrzebne RAZ
        na maszyne, przy wdrozeniu; lista agentow jest tym, po co sie tu wraca. Ekran zaczynajacy
        sie od wydawania kodu kazal przewijac obok czynnosci, ktora robi sie raz, do stanu, ktory
        sprawdza sie codziennie.
      */}
      <TokenChmurowegoMastera />

      <Wdrozenie />

      <SprzatanieWysp />

      <Dialog
        open={doOdinstalowania !== null}
        onOpenChange={(o) => {
          if (!o) {
            setDoOdinstalowania(null);
            setWpisanyWezel('');
          }
        }}
      >
        <DialogContent
          title={t('agents.odinstaluj.tytul')}
          description={doOdinstalowania === null ? undefined : `${doOdinstalowania.nodeId}${doOdinstalowania.hostname ? ` (${doOdinstalowania.hostname})` : ''}`}
        >
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{t('agents.odinstaluj.opis')}</p>
            <label className="flex flex-col gap-1 text-xs">
              <span>{t('agents.odinstaluj.potwierdz').replace('{wezel}', doOdinstalowania?.nodeId ?? '')}</span>
              <Input className="h-8 font-mono" autoComplete="off" value={wpisanyWezel} onChange={(e) => setWpisanyWezel(e.target.value)} />
            </label>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setDoOdinstalowania(null);
                setWpisanyWezel('');
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              /** ⚠️ Porównanie jak w API (po obcięciu spacji) — ekran nie może wpuścić tego, co serwer odrzuci. */
              disabled={odinstaluj.isPending || wpisanyWezel.trim() !== (doOdinstalowania?.nodeId ?? '')}
              onClick={() =>
                doOdinstalowania !== null && odinstaluj.mutate({ id: doOdinstalowania.id, potwierdzenie: wpisanyWezel.trim() })
              }
            >
              {odinstaluj.isPending ? <Loader2 className="size-4 animate-spin" /> : <PackageX className="size-4" />}
              {t('agents.odinstaluj.przycisk')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={doUsuniecia !== null} onOpenChange={(o) => !o && setDoUsuniecia(null)}>
        <DialogContent title={t('agents.deleteTitle')} description={doUsuniecia?.nodeId}>
          <p className="text-sm text-muted-foreground">{t('agents.deleteBody')}</p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDoUsuniecia(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={usunWezel.isPending}
              onClick={() => doUsuniecia !== null && usunWezel.mutate(doUsuniecia.id)}
            >
              {usunWezel.isPending ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
              {t('agents.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/*
        ═══════════════════════════════════════════════════════════════
         PODPOWIEDŹ NAPRAWY SCHEMATU — co uruchomić na bazie klienta.

         ⚠️⚠️ PORTAL TEGO NIE WYKONUJE I WYKONYWAĆ NIE BĘDZIE. Zakładanie klucza głównego to
         `ALTER TABLE` na produkcyjnej bazie klienta — poza tym, co agentowi wolno, i poza tym,
         co powinno dziać się bez człowieka. Okno pokazuje dokładnie to, co ktoś ma uruchomić.

         ⚠️⚠️ NAJPIERW SPRAWDZENIE POWTÓRZEŃ, POTEM ZAKŁADANIE, i ta kolejność jest treścią:
         `ALTER TABLE` na danych z powtórzonym kluczem PADNIE — lepiej, żeby padło na `SELECT`,
         który mówi ile i gdzie, niż w połowie okna serwisowego. Na Teleplanie zmierzone: zero
         powtórzeń w każdej z pięciu tabel, więc przejdzie bez czyszczenia danych.

         ⚠️ DOTYCZY JEDNEGO WĘZŁA. Wyspa Smart Synca ma po jednej bazie na węzeł i schemat bywa
         uszkodzony na części z nich — naprawa jednej bazy nie naprawia wyspy, więc okno nazywa
         węzeł i wyspę wprost.
        ═══════════════════════════════════════════════════════════════
      */}
      <Dialog open={naprawaFor !== null} onOpenChange={(v) => !v && setNaprawaFor(null)}>
        <DialogContent
          className="w-[min(48rem,calc(100vw-2rem))]"
          title={t('agents.naprawaTytul')}
          description={
            naprawaFor === null
              ? undefined
              : `${naprawaFor.wyspa} · ${naprawaFor.nodeId} · ${naprawaFor.dane.tabele.length} ${t('agents.naprawaTabel')}`
          }
        >
          {naprawaFor !== null && (
            <FormGrid>
              <p className="text-sm leading-snug">
                {t('agents.naprawaOpis').replace('{co}', naprawaFor.dane.skutki.join(', ') || '—')}
              </p>
              {/* ⚠️ Skutek i przyczyna w jednym zdaniu — bez tego to tylko lista nazw tabel. */}
              <p className="text-xs leading-snug text-muted-foreground">{t('agents.naprawaSkad')}</p>

              <div>
                <div className="mb-1 flex items-center justify-between gap-2">
                  <span className="text-xs font-medium">{t('agents.naprawaPolecenia')}</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void navigator.clipboard.writeText(naprawaFor.dane.polecenia.join('\n'))}
                  >
                    <Copy className="size-3.5" />
                    {/* ⚠️ Ten sam napis co przy kopiowaniu kodu rejestracyjnego wyżej — jedno słownictwo w oknie. */}
                    {t('agents.code.copy')}
                  </Button>
                </div>
                <pre className="max-h-72 overflow-auto rounded-md border border-border bg-muted/40 p-2 font-mono text-[11px] leading-relaxed">
{naprawaFor.dane.polecenia.join('\n')}
                </pre>
              </div>

              <p className="rounded-md border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-2 text-xs leading-snug">
                {t('agents.naprawaOstrzezenie')}
              </p>
            </FormGrid>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setNaprawaFor(null)}>
              {t('common.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * SPRZĄTANIE WYSP — karta, która powstała ze zgłoszenia z wdrożenia (2026-08-19).
 *
 * ⚠️ CAŁY PROBLEM W JEDNYM ZDANIU: rejestracja z kodem ZAŁOŻYCIELSKIM na maszynie z uszkodzonym
 * `SPSConfig.XML` zakłada NOWĄ WYSPĘ, więc dwie nieudane próby zostawiły u klienta trzy wyspy
 * w miejsce jednej. Vending+ pyta o dane wszystkich (widzi je na liście), a technik nie miał czym
 * ich usunąć — jedynym wyjściem było wystawienie instancji od nowa.
 *
 * ⚠️ LICZNIKI SĄ TU SEDNEM, NIE OZDOBĄ. Trzy wyspy mają tę samą nazwę, bo pochodzą z tego samego
 * kodu rejestracyjnego — nazwa nie powie, która jest prawdziwa. Powie to liczba automatów i wydań.
 */
function SprzatanieWysp() {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();
  const [doUsuniecia, setDoUsuniecia] = useState<WyspaDoSprzatania | null>(null);
  /**
   * KASOWANIE Z DANYMI I RESET INSTANCJI — dwie osobne rzeczy, dwa osobne stany.
   *
   * ⚠️⚠️ ZGŁOSZONE (01.09.2026): „czy jest jakaś opcja, żeby wyjebać wyspy do zera, bo jedna
   * instancja już tak się popierdoliła, że chciałbym to wywalić i mieć ją czystą". Do tej pory
   * ekran umiał usunąć WYŁĄCZNIE wyspę-widmo, a przy wyspie z danymi pokazywał zdanie o odmowie
   * i nic więcej — jedynym wyjściem było wystawienie instancji od nowa.
   *
   * ⚠️ POTWIERDZENIEM JEST PRZEPISANY TEKST, nie drugie kliknięcie. Przy wipie danych (Ustawienia)
   * wystarczy uzbrojony przycisk, bo agent odbuduje read-model; tutaj nie odbuduje się nic —
   * razem z wyspą znikają tokeny węzłów, więc każda maszyna wraca dopiero przez rejestrację.
   */
  const [doUsunieciaZDanymi, setDoUsunieciaZDanymi] = useState<WyspaDoSprzatania | null>(null);
  const [wpisanyKod, setWpisanyKod] = useState('');
  /** Odinstalowanie agentów z maszyn razem z wyspą — domyślnie tak (13.10.2026, decyzja usera). */
  const [odinstalujAgentow, setOdinstalujAgentow] = useState(true);
  const { user } = useAuth();
  /** ⚠️ Trasy kasujące mają `SdiOnlyGuard` — ekran musi je chować z tego samego powodu. */
  const isSdi = user?.clientId === null;

  const { data, isPending } = useQuery({ queryKey: ['wyspy-przeglad'], queryFn: fetchPrzegladWysp });

  const usun = useMutation({
    mutationFn: (id: string) => usunWyspe(id),
    onSuccess: (r) => {
      setDoUsuniecia(null);
      toast.success(
        t('agents.island.deleted').replace('{wyspa}', `${r.code}`).replace('{wezly}', String(r.usunieteWezly)),
      );
      void qc.invalidateQueries({ queryKey: ['wyspy-przeglad'] });
      void qc.invalidateQueries({ queryKey: ['agents'] });
      /** Przełącznik kontekstu sam wróci na pierwszą znaną wyspę — patrz `lib/islands.tsx`. */
      void qc.invalidateQueries({ queryKey: ['islands'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  /** Odświeżenie wszystkiego, co mówiło o usuniętej wyspie — przełącznik kontekstu też. */
  const odswiezPoKasowaniu = () => {
    void qc.invalidateQueries({ queryKey: ['wyspy-przeglad'] });
    void qc.invalidateQueries({ queryKey: ['agents'] });
    void qc.invalidateQueries({ queryKey: ['islands'] });
    void qc.invalidateQueries({ queryKey: ['enrollments'] });
  };

  const usunZDanymi = useMutation({
    mutationFn: ({ id, kod }: { id: string; kod: string }) => usunWyspeZDanymi(id, kod, odinstalujAgentow),
    onSuccess: (r) => {
      setDoUsunieciaZDanymi(null);
      setWpisanyKod('');
      if ((r.zleconeOdinstalowania ?? 0) > 0) {
        toast.success(t('agents.odinstaluj.zleconeWyspy').replace('{n}', String(r.zleconeOdinstalowania)));
      }
      toast.success(
        t('agents.island.forceDone')
          .replace('{wyspa}', r.code)
          .replace('{wierszy}', r.usunieteWiersze.toLocaleString('pl'))
          .replace('{wezly}', String(r.usunieteWezly))
          .replace('{obiekty}', String(r.usunieteObiekty)),
      );
      /**
       * ⚠️ OSIEROCONE PLIKI MÓWIMY OSOBNO, a nie chowamy w komunikacie sukcesu. Wiersze zniknęły,
       * więc z żadnego ekranu nie da się już tych plików zobaczyć — jedyne miejsce, gdzie ktoś
       * może się o nich dowiedzieć, to ta chwila.
       */
      if (r.nieusunieteObiekty > 0) {
        toast.error(t('agents.reset.leftovers').replace('{n}', String(r.nieusunieteObiekty)));
      }
      odswiezPoKasowaniu();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const wyspy = data?.wyspy ?? [];
  /**
   * Jedna wyspa i nie ma czego sprzątać — karta nie ma się po co pokazywać.
   *
   * ⚠️ DLA SDI KARTA JEST ZAWSZE, i to nie jest wyjątek dla wygody: to jedyne miejsce w portalu,
   * z którego da się usunąć wyspę Z DANYMI i wyczyścić instancję do zera. Schowana przy jednej
   * wyspie znaczyłaby „instancji z jedną popsutą wyspą nie da się posprzątać".
   */
  if (!isSdi && !isPending && wyspy.length < 2 && wyspy.every((w) => w.odmowaUsuniecia !== null)) return null;


  return (
    <section className="space-y-3">
      {/* ⚠️ BEZ AKAPITU WSTĘPNEGO — zgłoszone 01.09.2026: „dodajesz cały czas takie opisówki". */}
      <h2 className="text-sm font-medium">{t('agents.island.title')}</h2>

      {isPending ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          {t('common.loading')}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
              <tr>
                <th className="p-2">{t('agents.island.island')}</th>
                <th className="p-2 text-right">{t('agents.island.machines')}</th>
                <th className="p-2 text-right">{t('agents.island.employees')}</th>
                <th className="p-2 text-right">{t('agents.island.withdrawals')}</th>
                <th className="p-2 text-right">{t('agents.island.nodes')}</th>
                <th className="p-2" />
              </tr>
            </thead>
            <tbody>
              {wyspy.map((w) => (
                <tr key={w.id} className="border-t">
                  <td className="p-2">
                    {w.name}
                    <br />
                    <span className="font-mono text-[11px] text-muted-foreground">{w.code}</span>
                  </td>
                  <td className="p-2 text-right tabular-nums">{w.liczniki.maszyny}</td>
                  <td className="p-2 text-right tabular-nums">{w.liczniki.pracownicy}</td>
                  <td className="p-2 text-right tabular-nums">{w.liczniki.wydania.toLocaleString('pl')}</td>
                  <td className="p-2 text-right tabular-nums">
                    {w.liczniki.wezlyPotwierdzone}
                    {w.liczniki.wezlyInne > 0 ? (
                      <span className="text-muted-foreground"> (+{w.liczniki.wezlyInne})</span>
                    ) : null}
                  </td>
                  <td className="p-2 text-right">
                    {w.odmowaUsuniecia === null ? (
                      <Button size="sm" variant="ghost" onClick={() => setDoUsuniecia(w)}>
                        <Trash2 className="size-4" />
                        {t('agents.island.delete')}
                      </Button>
                    ) : (
                      <div className="flex flex-col items-end gap-1">
                        {/**
                          * ⚠️ POWÓD ODMOWY ZDANIEM, nie wyszarzonym przyciskiem. „Nie da się" bez
                          * wyjaśnienia kończy się telefonem, a odpowiedź jest jednym zdaniem:
                          * ta wyspa ma dane albo pracujący węzeł.
                          */}
                        <span className="text-[11px] text-muted-foreground">{w.odmowaUsuniecia.komunikat}</span>
                        {/**
                          * ⚠️ DRUGI PRZYCISK OBOK ZDANIA O ODMOWIE, a nie zamiast niego. Zdanie
                          * dalej mówi, DLACZEGO bezpieczna droga nie chce tej wyspy ruszyć —
                          * przycisk jest świadomym wyjściem poza nią, nie obejściem tamtej reguły.
                          */}
                        {isSdi && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive"
                            onClick={() => {
                              setWpisanyKod('');
                              setDoUsunieciaZDanymi(w);
                            }}
                          >
                            <Trash2 className="size-4" />
                            {t('agents.island.force')}
                          </Button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ⚠️ OKNO KASOWANIA Z DANYMI — potwierdzenie przepisanym kodem wyspy, patrz stan komponentu. */}
      <Dialog
        open={doUsunieciaZDanymi !== null}
        onOpenChange={(o) => {
          if (!o) {
            setDoUsunieciaZDanymi(null);
            setWpisanyKod('');
          }
        }}
      >
        <DialogContent
          title={t('agents.island.forceTitle')}
          description={doUsunieciaZDanymi === null ? undefined : `${doUsunieciaZDanymi.name} (${doUsunieciaZDanymi.code})`}
        >
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {t('agents.island.forceBody')
                .replace('{automaty}', String(doUsunieciaZDanymi?.liczniki.maszyny ?? 0))
                .replace('{pracownicy}', String(doUsunieciaZDanymi?.liczniki.pracownicy ?? 0))
                .replace('{wydania}', (doUsunieciaZDanymi?.liczniki.wydania ?? 0).toLocaleString('pl'))
                .replace(
                  '{wezly}',
                  String(
                    (doUsunieciaZDanymi?.liczniki.wezlyPotwierdzone ?? 0) + (doUsunieciaZDanymi?.liczniki.wezlyInne ?? 0),
                  ),
                )}
            </p>
            <p className="rounded border border-destructive/40 p-2 text-xs text-destructive">
              {t('agents.island.forceAgents')}
            </p>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={odinstalujAgentow} onChange={(e) => setOdinstalujAgentow(e.target.checked)} />
              <span>{t('agents.odinstaluj.zWyspa')}</span>
            </label>
            <label className="flex flex-col gap-1 text-xs">
              <span>{t('agents.island.forceConfirm').replace('{kod}', doUsunieciaZDanymi?.code ?? '')}</span>
              <Input
                className="h-8 font-mono"
                autoComplete="off"
                value={wpisanyKod}
                onChange={(e) => setWpisanyKod(e.target.value)}
              />
            </label>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setDoUsunieciaZDanymi(null);
                setWpisanyKod('');
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              /** ⚠️ Porównanie DOKŁADNE, tak samo jak w API — inaczej ekran wpuszczałby to, co serwer odrzuci. */
              disabled={usunZDanymi.isPending || wpisanyKod.trim() !== (doUsunieciaZDanymi?.code ?? '')}
              onClick={() =>
                doUsunieciaZDanymi !== null &&
                usunZDanymi.mutate({ id: doUsunieciaZDanymi.id, kod: wpisanyKod.trim() })
              }
            >
              {usunZDanymi.isPending ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
              {t('agents.island.force')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={doUsuniecia !== null} onOpenChange={(o) => !o && setDoUsuniecia(null)}>
        <DialogContent
          title={t('agents.island.deleteTitle')}
          description={doUsuniecia === null ? undefined : `${doUsuniecia.name} (${doUsuniecia.code})`}
        >
          <p className="text-sm text-muted-foreground">
            {t('agents.island.deleteBody').replace(
              '{wezly}',
              String((doUsuniecia?.liczniki.wezlyInne ?? 0) + (doUsuniecia?.liczniki.wezlyPotwierdzone ?? 0)),
            )}
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDoUsuniecia(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={usun.isPending}
              onClick={() => doUsuniecia !== null && usun.mutate(doUsuniecia.id)}
            >
              {usun.isPending ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
              {t('agents.island.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
